// ─────────────────────────────────────────────
//  TimelineChartPage — Gantt-style day view of all OCCUPIED rooms.
//
//  Rooms run down the left, grouped by facility section (Main OR, OB, Cath Lab,
//  Endo…) in board order; a time axis runs across the top for the selected date.
//  Each case renders as a bar from start→end, labelled with provider + procedure,
//  coloured by section. Bars are positioned in PERCENT of the day's span, so the
//  chart fits the viewport width with no horizontal scroll. A facility switcher
//  (timeline-local; doesn't change the app's active facility) + date picker drive
//  the data, pulled from the same hooks as the OR Board.
// ─────────────────────────────────────────────
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useFacilities } from "../hooks/useFacilities";
import { useAicByFacility } from "../hooks/useAicByFacility";
import { useIsMobile, useIsStacked } from "../hooks/useIsMobile";
import { rosterDateKey } from "../hooks/useDailyRoster";
import {
  syncLocumDeparture,
  listenToProcedureRefs,
  setDailyRoster,
  createTransfer,
  setRosterMemberDeparted,
  endRoomBreak,
} from "../firebase/firestore";
// `completeBreak` refuses to finish a break that was ASSIGNED and never
// started — the one transition that would record a meal as taken by somebody
// who never left the room. The ✓ below relies on that refusal rather than
// restating the rule.
import { completeBreak } from "../domain/breaks";
import useDeviceFacility from "../hooks/useDeviceFacility";
import useControlPanelData, { roomsMissingMd } from "../hooks/useControlPanelData";
import { useLiveConnection } from "../hooks/useLiveConnection";
import { useViewport, tvScaleForViewport } from "../hooks/useViewport";
import { alertSentAt, acknowledgerNames } from "../domain/alerts";
import { isTestModeBoot } from "../domain/testMode";
import useRoomCases from "../hooks/useRoomCases";
import { currentPhaseId, phaseLabel } from "../domain/casePhase";
import NextRoomModal from "../components/NextRoomModal";
import TimelineRail from "../components/timelineColumns/TimelineRail";
import TimelineMdCallRail, { buildRailMdCall } from "../components/timelineColumns/TimelineMdCallRail";
import { campusName } from "../components/timelineColumns/MdCallEditControls";
import DoctorReleaseMenu from "../components/timelineColumns/DoctorReleaseMenu";
import DoctorsNeededBox from "../components/timelineColumns/DoctorsNeededBox";
import TimelineOnCallCard from "../components/timelineColumns/TimelineOnCallCard";
import TimelineSidePanels from "../components/timelineColumns/TimelineSidePanels";
import { confirmRelief } from "../features/relief/confirmRelief";
import { assignCoverToRoom } from "../features/breaks/assignCoverToRoom";
// ⚠️ IMPORTED, NOT RE-TYPED. Every one of these is used inside `onAssignBreak`,
// where a missing binding compiles clean and throws at RENDER — the failure
// this file already records happening four times in one session.
import { chLabel } from "../hooks/useRoomBreakActions";
import { dayKey } from "../domain/dayKey";
import { breakCoverRecipients } from "../domain/breakDirective";
// The ONE room↔row join (full name, OR-prefix-stripped, ROOM_NAME_ALIASES).
import { assignmentsForRoom } from "../domain/gridTemplate";
import OutstandingFixModal from "../components/timelineColumns/OutstandingFixModal";
import TimelineTaskCard from "../components/timelineColumns/TimelineTaskCard";
import TimelineMaintenanceCard from "../components/timelineColumns/TimelineMaintenanceCard";
import { useTasks } from "../hooks/useTasks";
import DepartureFlow from "../components/DepartureFlow";
import BenchAssignFlow from "../components/BenchAssignFlow";
import AssignmentModal from "../components/AssignmentModal";
import CrossFacilityPicker from "../components/CrossFacilityPicker";
import { resolveFacilityRules } from "../domain/facilityRules";
import { useAlertCenter } from "../context/AlertContext";
import { useDayKey } from "../hooks/useDayKey";
import { testDoctors, testProviders } from "../config/testUsers";
import { useLocations } from "../hooks/useLocations";
import {
  blockStartingAt,
  coverageFor,
  adequacy,
  adequacyLabel,
  weekdayKey,
} from "../domain/coverageMap";
import { to12h, shiftLabelColor, SHIFT_LABEL_COLORS, ROOMS_PER_DOCTOR, hmToMinutes, orienteeBoardRows, rowClaimsAt } from "../domain/timeline";
import { taskRecipients } from "../domain/taskRecipients";
// ── DRAG TO RESCHEDULE / CLICK TO CREATE (2026-08-20) ────────────────────────
// Every line of it lives in siblings. This file is 1881 lines against a 500-line
// house rule, so a feature that could be added HERE is a feature that has to be
// added somewhere else: the bar (and its handles, tooltip and keyboard sliders)
// is now `CaseBar`, the release decision is `useCaseCommit`, the empty-lane click
// is `useLaneCreate`, and the measurement all three need is `useTrackGeometry`.
import CaseBar from "../components/timelineColumns/CaseBar";
import CaseDragStatus from "../components/timelineColumns/CaseDragStatus";
import useCaseCommit from "../components/timelineColumns/useCaseCommit";
import useLaneCreate from "../components/timelineColumns/useLaneCreate";
import useTrackGeometry from "../components/timelineColumns/useTrackGeometry";
// The LOWER lane of a paired room writes the second provider's leaving time —
// a different fact, a different write, and deliberately not the case commit.
import useSecondDepartCommit from "../components/timelineColumns/useSecondDepartCommit";
import useMovePreceptorOut from "../components/timelineColumns/useMovePreceptorOut";
import useSwapPair from "../components/timelineColumns/useSwapPair";
// A room row is as tall as the people in it — two providers need three lines
// inside one box, one provider comes back to the height it has always had.
import { roomRowHeight } from "../domain/caseLanes";
// Click a NAME on a bar → the quick-actions menu for THAT person, in the
// `BenchAssignFlow` this page already mounts for FREE NOW.
import useBarNameMenu from "../components/timelineColumns/useBarNameMenu";
import { nowLinePx } from "../domain/timelineGeometry";
import { writesAllowed, WRITE_BLOCKED_COPY } from "../domain/panelAssign";
import useRoomAutoRelease from "../components/timelineColumns/useRoomAutoRelease";
import FinishedRoomsCard from "../components/timelineColumns/FinishedRoomsCard";
import { boardEditGate } from "../domain/boardEditGate";
import { useToast } from "../context/ToastContext";
import LocumList from "../components/LocumList";
import TravelingBox from "../components/TravelingBox";
import { useStaff } from "../hooks/useStaff";
import { otherFacilities } from "../domain/facilities";
import { DEFAULT_FACILITY_ID } from "../config/roles";
import { sheetEngineState, SHEET_ENGINE_ENABLED_FIELD } from "../domain/sheetEngine";
import { useFacility } from "../hooks/useFacility";
import { updateFacility } from "../firebase/firestore";
import FacilityStaffingSummary from "../components/FacilityStaffingSummary";
import NavMenu from "../components/NavMenu";
import Avatar from "../components/Avatar";
import SignOutButton from "../components/SignOutButton";
import TimelineCaseModal from "../components/TimelineCaseModal";
import tokens from "../theme/tokens";

const { color, font, space, radius } = tokens;

export default function TimelineChartPage() {
  const navigate = useNavigate();
  const { user, realUser, isDoctor, canManageAssignments, isBoardManager, isAdmin, isScheduler, mustNotifyAIC, testingMode, isPreviewing } = useAuth();
  const toast = useToast();
  const isMobile = useIsMobile();
  // Whether the three columns can sit side by side at all. A separate question
  // from `isMobile` (768): the split needs ~1024 before the 179px rail is usable,
  // and the 768-1023 band is where the two answers differ. Measured at 834px the
  // rail's track was 92px and its content overflowed by 20px, painting alarm
  // colours over the chart.
  const isStacked = useIsStacked();

  // ── WALL DISPLAY ────────────────────────────────────────────────────────
  //
  // A corridor TV is read from three to five metres; a 2560px desk monitor is
  // read from sixty centimetres. Until now this page gave both the SAME type
  // sizes, because it keyed everything off width alone.
  //
  // ⚠️ SCALING BY WIDTH WOULD BE WRONG. It would enlarge type for someone
  // sitting two feet from a large monitor, who wants MORE board and not bigger
  // letters. Distance is not a width, so it is declared, not inferred — the app
  // already models it as `?tv=1`, the same switch `/board` uses, and a board
  // manager device (which IS a wall) gets it automatically.
  //
  // Reuses the existing machinery rather than growing a second one:
  // `tvScaleForViewport` (1080p = 1.0, clamped 0.7–2.2) and the `data-tv-mode`
  // body attribute that hides the global Messages launcher.
  const wantsTv =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("tv") === "1";
  const wallMode = Boolean(isBoardManager || wantsTv);
  const viewport = useViewport();
  const wallScale = wallMode ? tvScaleForViewport(viewport.width, viewport.height) : 1;

  useEffect(() => {
    if (!wallMode) return undefined;
    document.body.setAttribute("data-tv-mode", "1");
    window.dispatchEvent(new Event("atc-tv-mode"));
    return () => {
      document.body.removeAttribute("data-tv-mode");
      window.dispatchEvent(new Event("atc-tv-mode"));
    };
  }, [wallMode]);

  // Timeline-local facility selection (defaults to the app's active facility).
  const [facilityId, setFacilityId] = useState(user?.activeFacilityId || "default");
  // ⚠️ THE BOARD MUST ROLL OVER AT MIDNIGHT. This was `useState(rosterDateKey())`
  // — today's date captured ONCE at mount — so a board left open across 00:00
  // went on showing yesterday indefinitely. That is the wall-display case: this
  // page runs unattended for days, and yesterday's roster, assignments and task
  // directives looked exactly like today's.
  //
  // It follows the rollover ONLY while it is showing today. A date the operator
  // deliberately picked is theirs to keep — yanking them to the new day at
  // midnight because the clock ticked would be its own bug.
  const todayKey = useDayKey();
  const [date, setDate] = useState(todayKey);
  const lastTodayRef = useRef(todayKey);
  useEffect(() => {
    if (lastTodayRef.current === todayKey) return;
    const previousToday = lastTodayRef.current;
    lastTodayRef.current = todayKey;
    setDate((shown) => (shown === previousToday ? todayKey : shown));
  }, [todayKey]);

  // A Board Manager device is locked to its OWN campus — no cross-facility
  // browsing here (same scoping as its board).
  const myFacilityIds = Array.isArray(user?.facilityIds) ? user.facilityIds : [];

  // ⚠️ THE SWITCHER MUST ONLY OFFER FACILITIES THIS ACCOUNT CAN ACTUALLY READ.
  //
  // `facilities` is readable by any authenticated user (org metadata, no PHI),
  // but `rooms` and `assignments` are NOT: firestore.rules gates them on
  // `inFacility() || isAdmin() || isScheduler()`. So offering every active
  // facility here offered a choice the database then refused.
  //
  // What that looked like, observed live 2026-08-07 as a plain doctor at Wesley
  // Chapel selecting Tampa: four `permission-denied` snapshot errors in the
  // console, `useRooms` KEEPING ITS LAST SUCCESSFUL VALUE, and the page
  // rendering Wesley Chapel's rooms, cases and CRNA names under the heading
  // "AdventHealth Tampa" — with the rail's trust block stating Tampa in bold.
  // Every number on screen was real; every label about whose it was, was wrong.
  // Nothing threw where a user could see it.
  //
  // Filtering the list is the fix that removes the state entirely, rather than
  // detecting it afterwards. Admins and schedulers keep cross-facility browsing
  // because the rules genuinely permit them to read it.
  const canBrowseAllFacilities = isAdmin || isScheduler;
  // ONE subscription, two shapes. `useFacilities` opens a listener per call, so
  // the unfiltered list is captured once here and the picker's filtered view is
  // derived from it — the MD call badge needs every campus's name and colour,
  // including ones this viewer may not browse.
  const allFacilities = useFacilities();
  const facilities = allFacilities.filter(
    (f) =>
      f.isActive !== false &&
      (canBrowseAllFacilities ? true : myFacilityIds.includes(f.id))
  );
  // Live "now" marker — minutes since midnight, ticking every 30s. Declared
  // FIRST because the panel data below is driven by it: one tick for the whole
  // page means the chart, the rail and the boundary cards can never disagree
  // about what time it is.
  const [nowMin, setNowMin] = useState(() => {
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes();
  });
  useEffect(() => {
    const tick = () => {
      const d = new Date();
      setNowMin(d.getHours() * 60 + d.getMinutes());
    };
    const t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, []);
  const isToday = date === rosterDateKey();
  // The clock, but only on today's board — on any other date this is null and
  // `rowClaimsAt` then keeps every row, which is the fail-closed direction.
  const claimMin = isToday ? nowMin : null;

  // ONE hook for all of it.
  //
  // This page used to open its own `useRooms`, `useLocations`, `useDailyRoster`,
  // `useStaff` and `listenToAssignments`, and build `buildTimeline` itself — the
  // same five listeners and the same model `useControlPanelData` already
  // assembles, with IDENTICAL arguments. Adding the panel data alongside them
  // would have doubled roughly sixteen listeners (neither `useRooms` nor
  // `useStaff` has a module cache), so sourcing `model` FROM the hook is a net
  // reduction rather than a cost.
  // ⚠️ RESOLVED BEFORE THE HOOK, because the hook divides by it. `facilities` is
  // already in scope above; this used to sit ~270 lines further down, next to
  // the cards that read the other rules.
  const facilityDoc = facilities.find((f) => f.id === facilityId) || null;
  const facilityLabel = facilityDoc?.label || facilityId;
  const facilityRules = resolveFacilityRules(facilityDoc);

  // ── GOOGLE SHEET ENGINE ────────────────────────────────────────────────────
  //
  // ⚠️ ITS OWN LIVE READ, NOT `facilityDoc`. The facilities list this page
  // already holds is org metadata that changes when somebody edits a facility;
  // the engine's heartbeat changes every 60 SECONDS, and the whole value of the
  // rail pill is that it goes stale in front of you when the engine dies. Read
  // from a list that does not re-fetch, it would sit on the last value forever
  // and report a dead engine as running — the one lie this control exists to
  // prevent.
  const { facility: engineFacility } = useFacility(DEFAULT_FACILITY_ID);

  // ⚠️ A TICK, BECAUSE STALENESS IS THE ABSENCE OF A WRITE. A dying engine
  // produces no snapshot, so nothing re-renders and the pill would freeze on
  // "SYNCING · 12s ago" forever. Same reason the Setup panel keeps its own.
  const [engineTick, setEngineTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setEngineTick((n) => n + 1), 15000);
    return () => clearInterval(id);
  }, []);

  // `engineTick` is in the deps ON PURPOSE and is otherwise unused: it is what
  // re-runs this when NOTHING has changed, which is the whole point — staleness
  // is the absence of a write, so only a clock can reveal it.
  const engineState = useMemo(
    () => (engineFacility ? sheetEngineState(engineFacility) : null),
    [engineFacility, engineTick]
  );

  const [engineBusy, setEngineBusy] = useState(false);
  const toggleEngine = useCallback(async (next) => {
    setEngineBusy(true);
    try {
      await updateFacility(DEFAULT_FACILITY_ID, { [SHEET_ENGINE_ENABLED_FIELD]: next });
      toast.success(next
        ? "ATC app switched ON: sheet engine, nightly import and sweep resume."
        : "ATC app switched OFF: sheet engine, nightly import and sweep stop.");
    } catch (e) {
      // Surfaced verbatim: a refusal that reads as "something went wrong" sends
      // an admin to the wrong place, and this control is reached for when the
      // board is already going wrong.
      toast.error(`Could not change the ATC app switch: ${e?.message || e}`);
    } finally {
      setEngineBusy(false);
    }
  }, [toast]);

  // ⚠️ HOISTED ABOVE THE PANEL HOOK ON PURPOSE. The panel folds unanswered and
  // refused directives into NEEDS YOU NOW, so it needs today's tasks — and
  // passing them IN keeps `useTasks` a single subscription. Letting the panel
  // hook call `useTasks` itself would open a second listener on the same
  // day-scoped query, which is the duplicate-listener trap this file already
  // records for `useStaff`.
  const { tasks: taskDirectives, loading: taskDirectivesLoading } = useTasks(facilityId, date);

  const panel = useControlPanelData(facilityId, date, {
    nowMin,
    supervisionRatio: facilityRules.supervisionRatio,
    tasks: taskDirectives,
  });
  const {
    model,
    // The per-room lanes the outstanding list was folded from. The Fix screen
    // needs them because `model.placed` carries who-and-when but NO assignment
    // id, and the id is what the write goes to. Same source, so the fix acts on
    // exactly the cases the warning counted.
    lanes,
    rooms,
    // Needed by AssignmentModal's New Room branch. Referencing it without
    // destructuring it compiles fine and throws at render — the fourth time
    // that exact shape of mistake appeared in this session.
    locations,
    roster,
    assignments,
    staffDoctors,
    staffProviders,
    available,
    outstanding,
    boundary,
    mdCall,
    alertsCovered,
    alertFacilityId,
    alarms,
    railMetrics,
    // ⚠️ `alertRows` MUST stay in this list. It feeds the EMERGENCIES strip, and
    // the board's normal state is zero emergencies — so a missing binding would
    // have rendered a confident "0" that could never rise, and nothing on screen
    // would have looked wrong. Caught only by reading the destructure against
    // the usage, not by the build and not by the page appearing correct.
    alertRows,
    reliefRows,
    breakWaitingRows,
    breakRows,
  } = panel;

  // ── ORIENTEES LEFT THE BARS ────────────────────────────────────────────────
  //
  // Owner, 2026-08-24: *"you are going to place the orientis like batz and
  // briana… you will removed them from the time line"* — they now have their own
  // card under BREAKS, carrying the room and the leaving time.
  //
  // ⚠️ STRIPPED HERE, WHERE THE CHART IS DRAWN, NOT INSIDE `CaseBar`. The paired
  // bar — two bands, a hatch each, a second drag handle, a menu per name — is
  // still correct code with ~20 tests behind it, and turning it off at the
  // component would have invalidated all of them for a decision that is really
  // about THIS CHART. A future "put them back on the bar" deletes this one line.
  //
  // ⚠️ AND IT IS WHAT RETURNS THE ROW TO NORMAL HEIGHT — *"as soon as we remove
  // the extra crna come to normal size"* (owner, 2026-08-20). `roomRowHeight`
  // reads the same stripped cases, so a room with a trainee is 32px again.
  //
  // ⚠️ DISPLAY ONLY, AND THIS MATTERS. The orientee stays on the daily roster and
  // still counts as staffing cover — owner, same day: *"yes they are part of
  // daily roster"*, and on 2026-08-20, with the 2026-08-14 incident named:
  // "BOTH CRNAS COUNT AS STAFFING COVER." That counting reads the assignments
  // (`mergeSecondProviders`), never these bars, so nothing here can change it.
  // ⚠️ THE STRIP STAYS, AND THAT IS WHAT KEEPS THE ROW AT 32px. `roomRowHeight`
  // reads these same stripped cases, and `caseLanes` asks `c.orientee` whether a
  // room needs a second lane — owner, 2026-08-20: *"as soon as we remove the
  // extra crna come to normal size"*. Nulling the field is load-bearing for
  // both.
  //
  // ⚠️ AND THE PERSON IS STILL CARRIED, ON A DISPLAY-ONLY FIELD. Owner,
  // 2026-09-07, asked for the orientee to be visible in the room again — but as
  // a mark on the ONE bar, not a second band. `inlineOrientee` is read by
  // `caseBarLabel` and by nothing that measures, lays out or counts, so the
  // trainee reappears on screen without the row growing back.
  const barCase = useCallback(
    (c) => (c && c.orientee ? { ...c, orientee: null, inlineOrientee: c.orientee } : c),
    []
  );

  // One row per trainee per room: who, where, and when they go home.
  // `secondProviderOf` is THE resolver — the card must never re-read the cell.
  // Pure builder in domain/timeline — see `orienteeBoardRows`. It runs the SAME
  // clock as the break board, so a finished room releases its orientee and its
  // seat holder at the same minute instead of showing one room in two states.
  const orienteeRows = useMemo(
    () => orienteeBoardRows(assignments, claimMin),
    [assignments, claimMin]
  );

  // The four status strips. Every number here is REAL and comes from the same
  // feeds the rest of the page reads — no mock rows, no defaults. `alertsCovered`
  // decides whether the alert-derived three may be drawn at all; relief comes
  // from the assignments stream, which IS scoped to the facility on screen, so it
  // stays live either way.
  // Clearing an emergency is the ALL-CLEAR, so who may sound it is not a UI
  // preference: `canClear` is per-ALERT (the room's own supervising doctor, the
  // AIC for a floor-wide alert with no room doctor, admin as failsafe), and
  // firestore.rules enforces the same thing server-side.
  const { canClear: canClearAlert, resolve: resolveAlertById, resolveBreakRequestForRoom } = useAlertCenter();

  const statusCounts = useMemo(() => {
    // ⚠️ THE AGE IS THE ROW'S OWN, AND IT IS DATED AT THE SOURCE. This page used
    // to derive it here from `r.alert.sentAt` — which worked only while every
    // waiting row WAS an alert. A cover named by the AIC raises no alert (see
    // domain/breakWaiting), so an alert-derived age would have printed "age
    // unknown" for exactly the rows this fix was made to show. `breakWaitingRows`
    // now dates both kinds off the one day-gated clock the rest of the hook uses.
    //
    // The older hazard is still live and is why the field is read, never
    // recomputed: an earlier version read a `waitedMin` the row did not emit,
    // every value came back undefined, the list filtered to empty, and the
    // "longest N min" clause silently never rendered — a missing sentence looks
    // exactly like a sentence that had nothing to say.
    const waitRows = breakWaitingRows || [];
    const waits = waitRows.map((r) => r.waitedMin).filter((n) => Number.isFinite(n));

    // ⚠️ AGE MATTERS HERE MORE THAN COUNT, because an alert NEVER EXPIRES.
    // `listenToAlerts` queries only facilityId + status (firestore.js:1078) with
    // no date bound, and `isLiveAlert` is purely status-based — so any alert
    // nobody explicitly RESOLVED stays live forever, across days, and keeps
    // streaming to every board. A bare count would therefore read as things
    // happening right now when it can equally be things nobody closed last
    // month. `alertQueue` already sorts oldest-first, so the head is the oldest.
    const ageMin = (a) => {
      const d = a ? alertSentAt(a) : null;
      const ms = d instanceof Date ? d.getTime() : null;
      return Number.isFinite(ms) ? Math.max(0, Math.round((Date.now() - ms) / 60000)) : null;
    };
    return {
      emergencies: (alertRows || []).length,
      breaksWaiting: (breakWaitingRows || []).length,
      // The rows themselves, so the strip can name who is waiting rather than
      // only counting them.
      breakWaitingRows: waitRows,
      reliefInProgress: (reliefRows || []).length,
      // The rows themselves, so the strip can name who is relieving whom rather
      // than only counting.
      reliefRows: reliefRows || [],
      // The AGE is the actionable half of "3 waiting". Null rather than 0 when
      // no row carries one, so the card omits the clause instead of claiming
      // everybody has been waiting no time at all.
      oldestBreakWaitMin: waits.length ? Math.max(...waits) : null,
      oldestEmergencyMin: ageMin((alertRows || [])[0]),
      // WHO HAS PICKED EACH ONE UP. Acknowledgement is per-user and never
      // touches the shared board, so the count alone cannot say whether a
      // doctor is already walking to the room. Doctors and providers are both
      // in the pool: the acknowledger is whoever tapped it, and resolving only
      // doctors would print "name unknown" for a real, named person.
      emergencyRows: (alertRows || []).map((a) => {
        const ack = acknowledgerNames(a, [...(staffDoctors || []), ...(staffProviders || [])]);
        return {
          id: a.id,
          // Computed HERE, where the raw alert still has `roomDoctorId`. The
          // band is presentational and must not be handed an alert to judge.
          canClear: canClearAlert(a),
          // The alert carries its own `roomName` (AlertContext writes it at
          // send time); the live room is the fallback for older docs.
          roomName: a.roomName || (rooms || []).find((r) => r.id === a.roomId)?.name || "",
          ackNames: ack.names,
          ackCount: ack.count,
          ackUnnamed: ack.unnamed,
        };
      }),
    };
  }, [alertRows, breakWaitingRows, reliefRows, staffDoctors, staffProviders, rooms, canClearAlert]);

  // Locums on today's MD call list ("L" designation — sheet flag or staff
  // employmentType), resolved to full names via the staff pool. Feeds the
  // Locum List box below and the leave-aware doctor staffing math.
  // The staff pool now arrives from useControlPanelData above (see the listener
  // note there) — this page opens no staff listener of its own.
  const staffDoctorsPool = staffDoctors;
  const locums = useMemo(
    () =>
      (roster?.mdCallList || [])
        .filter((e) => e && e.code)
        .map((e) => {
          const match = staffDoctorsPool.find((d) => String(d.shortCode || "").toUpperCase() === e.code);
          return {
            code: e.code,
            name: match?.name || e.name || e.code,
            // "L" designation = the MD Call panel's L badge: an explicit locum
            // flag, a locums staff profile, OR no call number (how the sheet
            // marks locums — the badge renders "L" exactly for those).
            isLocum: Boolean(e.isLocum || match?.employmentType === "locums" || e.callOrder == null),
          };
        })
        .filter((e) => e.isLocum),
    [roster, staffDoctorsPool]
  );

  // `model` comes from useControlPanelData above, which calls buildTimeline with
  // exactly the same arguments this page used to pass (including
  // attachLocumLeaves, so the staffing math still drops a leaving locum from the
  // boundaries at/after their time). Still reactive by construction: assignments
  // stream live and the roster is a live hook, so any case add/remove/reschedule,
  // roster change, or locum leaving-time edit recomputes everything instantly.

  // Replacement pools for the relieve flow: today's rostered CRNAs and doctors
  // (AIC + supervisors), enriched from the staff master for shortCode/shiftCode.
  const crnaPool = (roster?.crnas || []).map((m) => ({ ...(staffProviders.find((p) => p.id === m.id) || {}), ...m }));
  const doctorPool = Array.from(
    new Map(
      [
        ...(roster?.aicId ? [{ id: roster.aicId, name: roster.aicName }] : []),
        ...(roster?.supervisors || []),
      ]
        .filter((m) => m && m.id)
        .map((m) => [m.id, { ...(staffDoctors.find((d) => d.id === m.id) || {}), ...m }])
    ).values()
  );

  // The case bar the user clicked to edit (times / relieve), or null.
  const [editCase, setEditCase] = useState(null);
  // The NEEDS YOU NOW row whose "Fix" button was pressed, or null. The whole
  // outstanding ITEM is carried (not a copy of its rooms), so the fix screen
  // reads `item.conflict` — the clash object the warning itself was built from.
  const [fixItem, setFixItem] = useState(null);
  // ⚠️ THE SAME FUNCTION `outstandingItems` WAS FED, not a second opinion.
  // `useControlPanelData` builds the "no supervising MD" item from
  // `roomsMissingMd(lanes)`; re-deriving "which rooms have no MD" here would be
  // a second rule that can disagree with the row the user just clicked.
  const missingMdRooms = useMemo(() => roomsMissingMd(lanes), [lanes]);
  // Which room's "next room" modal is open (the board's own NextRoomModal).
  const [nextRoomFor, setNextRoomFor] = useState(null);
  const canEditCases = canManageAssignments;

  // Confirming a staged handover from the RELIEF IN PROGRESS strip.
  //
  // ⚠️ THROUGH `confirmRelief`, NEVER A LOCAL PATCH. Confirming moves the
  // incoming person into the room's primary seat, and that fact lives in TWO
  // documents — the assignment row and the room doc. Writing only the row leaves
  // `room.providerId` pointing at the person who went home, and the room's chat
  // membership is built from that field: the relieved provider keeps receiving
  // the room's messages and the one standing in it never does. The screen
  // performing the action is the one that cannot see the bug, which is exactly
  // how that survived the last time.

  const [confirmingRelief, setConfirmingRelief] = useState(false);
  const onConfirmRelief = useCallback(
    async (row) => {
      if (!canManageAssignments || !row?.assignment) return;
      // The row already refuses when the incoming provider has gone home; this
      // is the second half of that guard, on the write side, because a control
      // that is merely hidden is not a rule.
      if (row.confirmable === false) return;
      setConfirmingRelief(true);
      try {
        await confirmRelief(row.assignment, { roomId: row.roomId });
      } catch (e) {
        console.error("Confirm relief failed:", e);
      } finally {
        setConfirmingRelief(false);
      }
    },
    [canManageAssignments]
  );

  // (nowMin / isToday are declared at the top of the component — the panel data
  // hook is driven by them, so they must exist before it runs.)

  // Locum auto-departure: when the clock reaches a locked leaving time,
  // remove that locum from the MD call roster on EVERY facility (outright —
  // the leave was scheduled, no Home-box pending). Idempotent server-side and
  // fired once per code per mount, so multiple open pages are harmless.
  const locumFiredRef = React.useRef(new Set());
  useEffect(() => {
    if (!isToday) return;
    for (const l of locums) {
      const min = hmToMinutes(roster?.locumLeaveTimes?.[l.code]);
      if (min != null && nowMin >= min && !locumFiredRef.current.has(l.code)) {
        locumFiredRef.current.add(l.code);
        syncLocumDeparture(date, l.code, user?.uid).catch((e) =>
          console.error("[LocumList] scheduled departure failed:", e)
        );
      }
    }
  }, [nowMin, locums, roster, isToday, date, user]);

  // Layout metrics — tuned per breakpoint so the chart stays legible + compact.
  // ── THE LATEST PHASE, PER ROOM ───────────────────────────────────────────
  // Owner, 2026-09-07: "in the time line add the latest status, for every
  // active room". The chart already shows WHO is in a room and for how long;
  // this is the missing half — WHERE IN THE CASE that room is.
  //
  // ⚠️ THE SAME DERIVATION THE BOARD AND THE PHONE USE. `currentPhaseId` is the
  // furthest-along stamped phase, not the most recently written one, so a
  // missed Induction filled in after Closing does not walk this label
  // backwards. Three surfaces, one rule.
  // ⚠️ `date`, THE DAY THE PAGE IS SHOWING — not today. This page lets an
  // operator pick a past or future day, and the phases have to follow the day
  // on screen or the rail would label a historical chart with this morning.
  const { openCaseFor } = useRoomCases(facilityId, date);
  const phaseForRoom = useCallback(
    (roomId) => {
      const id = currentPhaseId(openCaseFor(roomId)?.phases);
      return id ? phaseLabel(id) : "";
    },
    [openCaseFor]
  );

  const leftW = isMobile ? 92 : 152;
  // ── HOW TALL A ROOM ROW IS ───────────────────────────────────────────────
  //
  // ⚠️ NOT A CONSTANT ANY MORE, AND NOT STICKY. The owner lifted the fixed-height
  // rule on 2026-08-20 for exactly one case: *"if you want to expand the room is
  // ok, as soon as we remove the extra CRNA come to normal size."* A room with
  // two providers needs three lines inside one box (both people, then the
  // surgeon / supervising MD) and 24px bands so each name is a real target; the
  // moment the second provider is removed the row returns to 32/28, byte-
  // identical to what it has always drawn. `roomRowHeight` is asked per room,
  // at the row itself — see `domain/caseLanes`.
  const { axisStart, span, hourMarks } = model;

  // ── A ROOM THAT HAS FINISHED ITS DAY LETS GO OF ITSELF ──────────────────
  //
  // Owner, 2026-09-09: *"When the black time line pass the end of the or time
  // and no more cases to follow. Wait for m15 min, clear the room and remove it
  // from the time line and send the crna to unassigned"*.
  //
  // ⚠️ `claimMin`, NOT `nowMin`. It is null on any day that is not today, and
  // the rule has no opinion without a clock — which is what stops opening
  // yesterday's board from clearing yesterday's rooms.
  //
  // ⚠️ AND IT IS GATED ON `canEditCases`. A wall display and an observer render
  // this page too; neither may write, and a TV quietly clearing rooms all
  // evening would be the least debuggable bug in the app.
  const roomStatusOf = useCallback(
    (roomId) => (rooms || []).find((r) => r && r.id === roomId)?.status ?? null,
    [rooms]
  );
  // ⚠️ AND WHEN THAT STATUS WAS LAST WRITTEN. Owner, 2026-09-09, with rooms
  // sitting well past the now-line: *"The time line already pass those rooms,
  // those cases should disappear"*. They were held because their room docs still
  // read `active` — nobody walks the board at 20:00 marking finished cases done.
  // A status is not a clock; dated against the room's own last write, an
  // untouched `active` stops being evidence about now. Same basis as `nowMin`
  // (local hours × 60 + minutes), because the two are compared directly.
  const roomStatusAtOf = useCallback(
    (roomId) => {
      const at = (rooms || []).find((r) => r && r.id === roomId)?.updatedAt;
      // A Firestore Timestamp, a Date, or nothing at all on a doc nobody has
      // written since it was seeded — which is exactly the case that must NOT
      // hold a room.
      const d = at?.toDate?.() || (at instanceof Date ? at : null);
      return d ? d.getHours() * 60 + d.getMinutes() : null;
    },
    [rooms]
  );
  const {
    releaseIds: releasedRoomIds,
    confirmRows: finishedRoomRows,
    busyId: closingRoomId,
    closeRoom,
    keepOpen,
  } = useRoomAutoRelease({
    sections: model.sections,
    roomStatus: roomStatusOf,
    roomStatusAt: roomStatusAtOf,
    nowMin: claimMin,
    enabled: canEditCases,
    facilityId,
    date,
    updatedBy: user?.uid || null,
    onError: useCallback(
      (err, roomId) => {
        console.error("Could not close room", roomId, err);
        // ⚠️ SAID OUT LOUD. A room that stays on the chart after the board
        // decided it was finished is indistinguishable from one that was never
        // finished, and the operator would never know a write had failed.
        toast.error("Could not close a finished room. It is still on the board.");
      },
      [toast]
    ),
  });

  // What the CHART draws: everything except the rooms that have just been let
  // go. ⚠️ THE FILTER IS ON THE RENDER, NOT ON `model`. `useCaseCommit` and the
  // staffing maths below read `model.sections` and `model.placed`, and a commit
  // that could not see a room would write against a chart the rest of the app
  // does not share. A released room disappears from the picture; it does not
  // disappear from the day.
  const visibleSections = useMemo(() => {
    if (releasedRoomIds.size === 0) return model.sections;
    return model.sections
      .map((sec) => ({ ...sec, rooms: sec.rooms.filter((r) => !releasedRoomIds.has(r.id)) }))
      .filter((sec) => sec.rooms.length > 0);
  }, [model.sections, releasedRoomIds]);
  // The header counts what the chart shows, or it contradicts it.
  const visibleRoomCount = useMemo(
    () => visibleSections.reduce((n, sec) => n + sec.rooms.length, 0),
    [visibleSections]
  );

  // 1px vertical gridline every hour, drawn behind the bars (no extra DOM).
  const stepPct = span > 0 ? (60 / span) * 100 : 100;
  const gridBg = `repeating-linear-gradient(to right, ${color.border.default}66 0 1px, transparent 1px ${stepPct}%)`;

  // ── WHERE THE TRACK ACTUALLY STARTS ─────────────────────────────────────
  //
  // ⚠️ THIS WAS A REAL BUG, and the now-line was where it showed. A chart row is
  //     [ room label : leftW ][ row buttons : ~64px ][ track : flex 1 ]
  // and the row buttons exist whenever the viewer may edit. The old formula was
  // `calc(${leftW}px + (100% - ${leftW}px) * frac)` — it knew about the label
  // column and nothing else, so on an editable board the "now" marker was drawn
  // ~64px LEFT of the minute it claimed, and it ignored the chart's own 12px
  // padding at both ends on top of that. On a wall board that line is how a room
  // reads where it stands in the day.
  //
  // The axis label row was wrong by the SAME offset for the same reason, so both
  // are fed from one measurement now — fixing only the line would have put it
  // under the wrong hour label and looked like a new bug.
  const { chartRef, geom } = useTrackGeometry();
  const showNow = isToday && span > 0 && nowMin >= axisStart && nowMin <= axisStart + span;
  const nowFrac = span > 0 ? (nowMin - axisStart) / span : 0;
  const measuredNowPx = geom
    ? nowLinePx({ offsetPx: geom.offsetPx, trackWidth: geom.trackWidth, nowMin, axisStart, span })
    : null;
  // Before the first layout (and under jsdom, which has none) keep the old
  // approximation rather than drawing at zero.
  const nowLeft =
    measuredNowPx != null ? `${measuredNowPx}px` : `calc(${leftW}px + (100% - ${leftW}px) * ${nowFrac})`;

  const alertHomeFacilityName =
    facilities.find((f) => f.id === alertFacilityId)?.label || null;

  // ── TASK DIRECTIVES ─────────────────────────────────────────────────────
  //
  // The board→phone one-way lane. The PAGE owns the subscription (as it owns
  // every other listener here) and the card stays a renderer, matching the
  // sibling panels. `useTasks` is scoped by facility AND day and subscribes to
  // nothing when either is missing.

  // Who may be picked: today's roster, doctors first (they are the shorter
  // list and the more likely target of a typed instruction). Not memoized on
  // purpose — `doctorPool` / `crnaPool` are rebuilt on every render above, so a
  // useMemo keyed on them would memoize nothing while implying it did.
  //
  // ⚠️ AND MARKED FOR PRESENCE, NOT ONLY FOR REACHABILITY. The picker's only
  // mark used to be "no app account", which answers "is there a phone?" and
  // says nothing about "is the person here?". A CRNA who left at 15:00 rendered
  // identically to one standing in WOR 4, so at 16:30 an AIC covering a room
  // could pick the first familiar name, reach a phone at home, and watch the
  // ledger sit on "pending" while the room stayed uncovered. `roster.departed`
  // is the same map the staffing count 700 lines below already trusts.
  //
  // ⚠️ MARKED, NEVER FILTERED OUT. A departed clinician can be called back, and
  // a name missing from a roster reads as a bug — the doctrine the card already
  // follows for people with no app account.
  //
  // NOT memoized, for the same reason the pools above are not: `doctorPool` and
  // `crnaPool` are rebuilt on every render, so a useMemo keyed on them would
  // memoize nothing while implying it did — and one keyed on `roster` instead
  // would need a disable comment for a rule this project's a11y config does not
  // even enable. The work is a map over today's roster; it is cheap.
  // Who a directive may be sent to. The rule lives in `domain/taskRecipients`
  // so it is testable — this page has no test file, which is exactly how a rule
  // of this kind went unguarded before (see the note on `orienteeBoardRows`).
  const taskPeople = taskRecipients({ doctors: doctorPool, crnas: crnaPool }, roster);

  // The pool the card resolves staff ids → auth uids through. Sourced from the
  // panel hook's staff pools rather than a second `useStaff()` call, which
  // would open a duplicate whole-collection listener (this hook has no module
  // cache — see the note on useControlPanelData above).
  const taskStaff = useMemo(
    () => [...(staffDoctors || []), ...(staffProviders || [])],
    [staffDoctors, staffProviders]
  );

  // Naming a break cover straight from the BREAKS card.
  //
  // ⚠️ THE CARD USED TO SEND YOU TO ANOTHER PAGE. Every chip navigated to
  // /board?room=…, so naming a cover meant leaving the board you were running,
  // finding the room, and coming back — for a decision that is four taps of
  // context. The write itself goes through the SHARED `assignCoverToRoom`, the
  // same one `useRoomBreakActions` uses, so the card and the room card cannot
  // disagree about what "assign a break" means.
  // ⚠️ A BREAK REQUEST MUST BE WITHDRAWABLE. A provider taps "I need a break",
  // the bench turns out to be empty, and the red chip then sat on the board with
  // NO WAY BACK: the request outlived the reason for it, and the only exit was
  // covering a break nobody may still want. The request lives ONLY in the alert
  // bus — `raiseBreakRequest` never writes the room doc — so withdrawing it is
  // resolving those alerts, not a break-state transition.
  const onCancelRequest = useCallback(
    async ({ row }) => {
      if (!canManageAssignments || !row?.roomId) return false;
      try {
        await resolveBreakRequestForRoom(row.roomId);
        return true;
      } catch (e) {
        // The machine's own words: "permission-denied" tells the reader what to
        // do and "could not cancel" does not.
        console.error("Cancel break request failed:", e);
        return false;
      }
    },
    [canManageAssignments, resolveBreakRequestForRoom]
  );

  // THE ✓ ON A BREAK ROW. Owner, 2026-09-11: "we need put a check mark in the
  // right side to help clear the breaks." Ending a break meant opening the
  // room, and with fourteen people waiting on a meal that is fourteen round
  // trips through another page.
  //
  // ⚠️ ONE WRITE, TWO MEANINGS, AND THE CARD HAS ALREADY NAMED WHICH. If the
  // channel is RUNNING this ends it and the reliever is released; if it is
  // `none` this records a meal that was taken off the board, with no cover
  // named. Both are `completeBreak` + `endRoomBreak` — the same pair the room's
  // own "End break" uses, so the card and the room cannot come to disagree
  // about what finishing a break means.
  //
  // ⚠️ AND IT GOES THROUGH `completeBreak`, WHICH THROWS ON `assigned`. A break
  // somebody was asked to cover and never started cannot be "finished" —
  // completing it would put a meal in the record for a provider who never left
  // the room. The card does not offer those channels; this is the second lock.
  const onClearBreak = useCallback(
    async ({ row, channel }) => {
      if (!canManageAssignments || !row?.roomId || !channel) return false;
      const roomDoc = (rooms || []).find((r) => r && r.id === row.roomId);
      if (!roomDoc) return false;
      try {
        const breaks = completeBreak(roomDoc.breaks || {}, channel);
        await endRoomBreak(roomDoc.id, { breaks, channel });
        // A request still standing for this room is answered by the break
        // having happened — leaving it would keep the board asking for a
        // colleague nobody needs any more.
        try { await resolveBreakRequestForRoom(roomDoc.id); } catch (e) { console.error(e); }
        return true;
      } catch (e) {
        // The machine's own words: "assigned but never started" tells the
        // reader what to do and "could not clear" does not.
        console.error("Clear break failed:", e);
        toast.error(e?.message || "Could not clear that break.");
        return false;
      }
    },
    [canManageAssignments, rooms, resolveBreakRequestForRoom, toast]
  );

  const onAssignBreak = useCallback(
    async ({ row, channel, giver }) => {
      if (!canManageAssignments) return false;
      const roomDoc = (rooms || []).find((r) => r && r.id === row?.roomId);
      if (!roomDoc) return false;
      setConfirmingRelief(true);
      try {
        // WHO TO TELL. Naming a cover wrote the room doc and told NOBODY:
        // there is no Firestore trigger on `rooms`, so the CRNA just named on
        // this card never heard about it and the AIC still reached for the
        // phone. Three recipients, one of whom is asked to answer — see
        // domain/breakDirective.
        // ⚠️ THE SHARED JOIN, NOT A HAND-ROLLED ONE. Rooms are named "OR 3" or
        // "WOR 2" while a row's `orRoom` is "3" or "WC E2", and
        // `assignmentsForRoom` ranks full-name, OR-prefix-stripped AND
        // ROOM_NAME_ALIASES matches in one place. A local comparison here
        // handled only the middle case, so on any aliased room it found nothing
        // and the supervising anaesthesiologist was silently never told.
        const asg =
          assignmentsForRoom(roomDoc.name, assignments).find((a) =>
            rowClaimsAt(a, claimMin)
          ) || null;
        await assignCoverToRoom(roomDoc, {
          channel,
          giver,
          notify: {
            facilityId,
            // The FACILITY's day — the same key `useTasks` reads back.
            dateKey: dayKey(),
            senderId: user?.uid || null,
            senderName: user?.name || "",
            roomName: roomDoc.name || "",
            channelLabel: chLabel(channel),
            giverName: giver?.name || "",
            relievedName: row?.name || "",
            // The ONE recipient rule, shared with useRoomBreakActions.
            ...breakCoverRecipients({
              staff: taskStaff,
              room: roomDoc,
              assignment: asg,
              giverId: giver?.id,
              relievedId: row?.id,
            }),
          },
        });
        return true;
      } catch (e) {
        // The machine's own message, verbatim — "lunch already has a cover
        // assigned" tells the reader what to do; "could not assign" does not.
        console.error("Assign break failed:", e);
        return false;
      } finally {
        setConfirmingRelief(false);
      }
    },
    [canManageAssignments, rooms, assignments, claimMin, facilityId, user, taskStaff]
  );

  // ⚠️ THREE WAYS TO COMPOSE A DIRECTIVE THAT NOBODY WILL EVER SEE, all of them
  // reachable from this page's own controls, none of them visible in the
  // resulting document:
  //   · a read-only viewer's write is refused by the rules;
  //   · this page has a free DATE picker, and the lane is day-scoped — a
  //     directive stamped with yesterday is filtered out of every phone by the
  //     listener itself, so it would be "sent" and invisible;
  //   · this page has a free FACILITY switcher, and the create rule requires
  //     the sender to be in the facility on the document.
  // Each is named rather than merely disabled, so the operator knows what to
  // change instead of wondering why the button is grey.
  const composeBlockedReason = !canEditCases
    ? "Read-only here. Sending a directive is limited to the people who can change the board."
    : !isToday
    ? "Directives are day-scoped, so one dated in the past reaches nobody. Switch back to today to send."
    : facilityId !== user?.activeFacilityId
    ? `You are browsing ${facilityLabel}. A directive can only be sent from the campus you are signed in to.`
    : null;

  // ── COLUMN DATA ─────────────────────────────────────────────────────────
  //
  // Everything below feeds columns 1 and 5-7. All of it is derived from `panel`
  // and the facility doc; no new listener is opened.
  const connection = useLiveConnection(facilityId);

  // The facility's configured surgeons, for the case modal's surgeon picker —
  // the SAME list the Daily List grid and the Schedule page offer, so the three
  // surfaces cannot drift into different spellings of one surgeon.
  //
  // Subscribed only while a case modal can be opened: on a wall display nobody
  // is editing, and this is a listener per open board otherwise.
  const [surgeons, setSurgeons] = useState([]);
  useEffect(() => {
    if (!facilityId || !canEditCases) return undefined;
    return listenToProcedureRefs(
      facilityId,
      (r) => setSurgeons([...(r.surgeons || [])].sort((a, b) => a.localeCompare(b))),
      (e) => console.error("[Timeline] procedure_refs listener failed:", e)
    );
  }, [facilityId, canEditCases]);

  // Rooms with a LIVE emergency, for the blinking room label in the chart. The
  // SAME Set the rail's EMERGENCIES chip counts and the boards paint red, so
  // the chart cannot disagree with them about which room is in trouble.
  //
  // ⚠️ It refuses when the alert bus covers another campus. A room quietly NOT
  // blinking on a board that is not receiving that facility's alerts would read
  // as "this room is fine", which is the one thing it must never say.
  const emergencyRooms = alertsCovered ? alarms?.emergencyRoomIds : null;

  // ⚠️ THE FOUR RAIL CHIPS ARE GONE, REPLACED BY ONE BOX (product owner,
  // 2026-09-07). EMERGENCIES and BREAKS WAITING keep their home in
  // TimelineStatusBand, which reflows 3-2-1 columns but never disappears, so
  // nothing was lost. UNCOVERED had no second home on this page and survives as
  // a LINE inside DoctorsNeededBox. SHORT n AT 3 PM was the same doctor maths
  // this box now does at the day's real crossings instead of four fixed hours.
  //
  // `panelChips` is left in domain/controlPanel with its tests: it is pure, and
  // deleting the old surface before the new one has been verified live is how
  // you end up with neither.
  const uncoveredCount = alertsCovered ? alarms?.uncoveredRoomIds?.size ?? 0 : null;

  // NOTE: the coverage map is read ONLY by the Staffing Adequacy cards further
  // down (see `blockStartingAt` / `coverageFor` there). It deliberately does not
  // feed the rail or the boundary card: its single relationship to this page is
  // providers-on-shift vs rooms-needing-coverage. An earlier draft also drew a
  // room-ceiling readout in the rail and derived per-facility shift boundaries
  // from the blocks; both were removed on 2026-08-08 when the blocks became a
  // fixed five shared by every facility.
  // ── AXIS LABEL DENSITY, FROM A MEASUREMENT ──────────────────────────────
  //
  // ⚠️ A CALLBACK REF, NOT `useEffect(..., [])`. The axis lives inside the
  // `occupiedCount === 0 ? empty : chart` branch, so on first render the node
  // does not exist yet. An effect keyed on `[]` would run once, find nothing,
  // and measure nothing — silently, forever. A callback ref fires when the node
  // actually attaches, and again when it detaches.
  const [axisWidth, setAxisWidth] = useState(0);
  const axisObserver = React.useRef(null);
  const measureAxis = React.useCallback((node) => {
    if (axisObserver.current) {
      axisObserver.current.disconnect();
      axisObserver.current = null;
    }
    if (!node) return;
    setAxisWidth(node.getBoundingClientRect().width);
    if (typeof ResizeObserver === "undefined") return; // jsdom
    const ro = new ResizeObserver(([entry]) => {
      if (entry) setAxisWidth(entry.contentRect.width);
    });
    ro.observe(node);
    axisObserver.current = ro;
  }, []);
  useEffect(() => () => axisObserver.current?.disconnect(), []);

  // "12:30 PM" at 11px is ~52px; a bare "7 AM" is ~34px. 52 leaves a real gap
  // between neighbours rather than letting them touch.
  const AXIS_LABEL_MIN_PX = 52;
  const axisStep = useMemo(() => {
    const marks = model.hourMarks?.length || 0;
    if (!marks) return 1;
    // Before the first measurement, fall back to the old heuristic so the very
    // first paint still shows a sensible axis.
    if (!axisWidth) return isMobile ? 3 : marks > 12 ? 2 : 1;
    const maxLabels = Math.max(2, Math.floor(axisWidth / AXIS_LABEL_MIN_PX));
    return Math.max(1, Math.ceil(marks / maxLabels));
  }, [axisWidth, model.hourMarks, isMobile]);

  // Which hour marks actually get a label.
  //
  // The day's LAST mark is always worth keeping — it is when the board ends —
  // but "every Nth, plus always the last" can drop the last one right beside the
  // previous kept label. That is what produced the surviving "5 PM|6 PM"
  // overprint at 1280 after the step itself was fixed. When the tail is closer
  // than half a step, the last mark REPLACES its neighbour instead of joining it.
  const axisKeep = useMemo(() => {
    const marks = model.hourMarks?.length || 0;
    if (!marks) return new Set();
    const kept = [];
    for (let i = 0; i < marks; i += axisStep) kept.push(i);
    const last = marks - 1;
    if (kept[kept.length - 1] !== last) {
      // Compare against a FULL step, not half of one. Half was not enough: with
      // step 2 the tail sits exactly 1 mark from its neighbour, `1 < 1` is
      // false, and the two labels were appended adjacent — which is precisely
      // the "5 PM|6 PM" overprint this rule exists to prevent. Anything closer
      // than one step is by definition tighter than the spacing the step was
      // chosen to guarantee.
      if (last - kept[kept.length - 1] < axisStep) kept[kept.length - 1] = last;
      else kept.push(last);
    }
    return new Set(kept);
  }, [model.hourMarks, axisStep]);

  // ── DEPART, FROM THE MD CALL LIST ───────────────────────────────────────
  //
  // The SAME component the Daily List already uses (`DepartureFlow`, wired in
  // DailyAssignmentPage:466). Departing a doctor reassigns their rooms, may
  // require an AIC handoff, and writes across facilities — it is emphatically
  // not a button to reimplement next to a second copy of the rules.
  //
  // ⚠️ DepartureFlow READS `person.role` and `person.id` (isDoctor,
  // requiresAicHandoff, activeRoomsFor). The MD-call ROWS are a display shape —
  // `mdCallRows` builds {name, callOrder, isLocum, leaveLabel} and carries no
  // role — so passing a row straight in would silently take the non-doctor path
  // and skip the AIC handoff. Resolve each row back to the real person first.
  const departedDoctors = roster?.departed || {};
  const personForMdRow = useCallback(
    (row) => {
      if (!row?.id) return null;
      // Locum rows the sheet invented carry a synthetic `mdcall:CODE` id and
      // exist on no roster; they leave via the Locum List's leaving-time, which
      // is a different mechanism with its own cross-facility sync.
      if (String(row.id).startsWith("mdcall:")) return null;
      const staff = (staffDoctors || []).find((d) => d && (d.id === row.id || d.uid === row.id));
      if (!staff) return null;
      return { ...staff, id: row.id, name: row.name || staff.name };
    },
    [staffDoctors]
  );

  const renderDepart = useCallback(
    (row) => {
      const person = personForMdRow(row);
      if (!person) return null;
      return (
        <DepartureFlow
          person={person}
          assignments={assignments}
          roster={roster}
          aicId={roster?.aicId}
          facilityId={facilityId}
          date={date}
          staffPool={{ doctors: staffDoctors || [], crnas: staffProviders || [] }}
          actor={{ uid: user?.uid, name: user?.name }}
          // Compact ✕ here: the MD Call List is a reference card in a narrow
          // column, and four full-width red buttons dominated it. The Daily
          // List keeps the worded button, where the action IS the row.
          compact
        />
      );
    },
    [personForMdRow, assignments, roster, facilityId, date, staffDoctors, staffProviders, user]
  );

  // Sending a bench CRNA home at the end of their shift.
  //
  // ⚠️ THE SAME `DepartureFlow` THE MD RAIL AND THE DAILY LIST ALREADY RENDER.
  // A departure reassigns the person's rooms and can require an AIC handoff;
  // that is emphatically not a `departed[id] = true` written from a side panel.
  // Owner asked for "send home like the daily list does", which is this
  // component, unchanged.
  //
  // ⚠️ AND `role` IS SUPPLIED. `DepartureFlow` branches on `person.role`
  // (isDoctor → the AIC-handoff path), and a roster CRNA entry does not always
  // carry one — an undefined role would take the wrong branch silently.
  const renderSendHome = useCallback(
    (member) => {
      if (!member?.id) return null;
      return (
        <DepartureFlow
          person={{ ...member, role: member.role || "provider" }}
          assignments={assignments}
          roster={roster}
          aicId={roster?.aicId}
          facilityId={facilityId}
          date={date}
          staffPool={{ doctors: staffDoctors || [], crnas: staffProviders || [] }}
          actor={{ uid: user?.uid, name: user?.name }}
          // Compact for the same reason the MD rail is: this is a narrow column
          // and a full-width red button would dominate a housekeeping list.
          compact
        />
      );
    },
    [assignments, roster, facilityId, date, staffDoctors, staffProviders, user]
  );

  // ── MD CALL, RAIL COPY (column 1) ───────────────────────────────────────
  //
  // The Daily List's view of the same call list: post-call slots, locums, and
  // the campus each doctor is working at carried on the number badge. The
  // right-hand card answers "who do I ring and who has left"; this one answers
  // "who is on today, and where". Both are fed the SAME departure props, so
  // they cannot disagree about who has gone home.
  //
  // Cross-campus colour needs each facility's daily board. The hook fails soft
  // (a denied read just drops the tint), but it opens one listener per campus,
  // so it is only asked for by roles that can act on the answer.
  const facilityBoards = useAicByFacility(date, {
    enabled: canManageAssignments || canBrowseAllFacilities,
  });
  // The LIVE AIC, not the sheet's import-time flag — after an AIC handoff the
  // tag has to move with the roster (same rule DailyAssignmentPage:443-447).
  const liveAicCode = String(
    (staffDoctors || []).find((d) => d && (d.id === roster?.aicId || d.uid === roster?.aicId))
      ?.shortCode || ""
  ).toUpperCase();
  const railMdCall = useMemo(
    () =>
      buildRailMdCall({
        callList: roster?.mdCallList || [],
        mdCall,
        staffDoctors,
        aicCode: liveAicCode,
      }),
    [roster, mdCall, staffDoctors, liveAicCode]
  );

  // ── EDITING THE RAIL'S MD CALL, GATED ON THE DEVICE'S CAMPUS ────────────
  //
  // The rail lists EVERY campus (that is its job), so the only thing that makes
  // editing safe on it is knowing which campus this device is physically at.
  // `useDeviceFacility` RESOLVES that and FAILS CLOSED — no fix, a denied
  // permission, no configured fence, or two overlapping radii all resolve to
  // nothing. It is emphatically NOT `checkGeofence`, which verifies a facility
  // it was handed and deliberately fails OPEN so a fenceless tenant can still
  // load its board. Fail-open is correct there and would be a licence to edit
  // another hospital's roster here.
  //
  // It writes nothing and switches nothing: the hook only ever CONFIRMS, and
  // the rail compares its answer against the campus this page is showing.
  const deviceFacility = useDeviceFacility({
    facilities: allFacilities,
    accountFacilityId: user?.activeFacilityId || null,
    allowedFacilityIds: myFacilityIds,
    isAdmin,
  });

  // ⚠️ THE SAME GATE THE MD CALL RAIL USES, not a second opinion about the same
  // device. Releasing a doctor from the Doctors Needed box writes to the very
  // roster that card's ✕ Depart writes to, so the two controls sitting inches
  // apart on one rail must never disagree about whether this device may act.
  // Both call domain/boardEditGate with the same inputs; the rail computes its
  // own from the props it is given, which keeps its prop contract intact.
  //
  // The DAY condition is not here: the box hides the control entirely on any
  // board but today's and says why in its own words ("Projection only — this is
  // not today's board"), which is a better answer than a lock note.
  const doctorsGate = boardEditGate({
    canEditBoard: canManageAssignments,
    deviceFacility,
    viewedFacilityId: facilityId,
    isAdmin,
    campusName: (id) => campusName(id, allFacilities),
  });

  const [releaseOpen, setReleaseOpen] = useState(false);
  const [transferBusyKey, setTransferBusyKey] = useState(null);

  // ⚠️ FILTERED THE SAME WAY THE STAFFING SUMMARIES ARE (:1562). `otherFacilities`
  // is a hardcoded tenant list that does NOT consult `user.facilityIds`, so an
  // unfiltered destination list offers a transfer the rules then refuse — a
  // control that looks live and is not.
  const doctorDestinations = useMemo(
    () =>
      otherFacilities(facilityId)
        .filter((f) => canBrowseAllFacilities || myFacilityIds.includes(f.id))
        .map((f) => ({ id: f.id, label: f.label })),
    [facilityId, canBrowseAllFacilities, myFacilityIds]
  );

  // ⚠️ WHY THE SURPLUS IS NOT RELEASABLE, in words. `releasable` is available
  // now minus the PEAK of the rest of the day, so it is routinely 0 while
  // `delta` is positive — and a bare "None to release" beside two visibly idle
  // doctors reads as a broken board. Naming the peak that is holding them turns
  // a refusal into something a manager can plan around.
  const doctorHoldNote = useMemo(() => {
    const plan = panel.doctorPlan;
    const now = plan?.now;
    if (!now || (plan?.releasable ?? 0) > 0) return "";
    if (now.delta <= 0) return "";
    const peak = plan.peakAhead;
    if (!Number.isFinite(peak) || peak <= 0) return "";
    const spare = now.delta === 1 ? "1 doctor is spare" : `${now.delta} doctors are spare`;
    // ⚠️ `peakAtLabel`, NOT a lookup in `steps`. The climb holding these doctors
    // back is usually one the box deliberately does not list, so searching the
    // rendered steps would find nothing precisely when this note matters most.
    return plan.peakAtLabel
      ? `${spare} right now, but the day climbs back to ${peak} MD at ${plan.peakAtLabel}.`
      : `${spare} right now, but the rest of the day still needs ${peak} MD.`;
  }, [panel.doctorPlan]);

  // Naming the day's AIC (Tampa reads "DOD" — the label is per campus).
  //
  // ⚠️ THIS WRITE IS EXPECTED TO BE REFUSED FOR SOME OF THE PEOPLE WHO CAN SEE
  // THE BUTTON. firestore.rules locks a change to `aicId` on a live board to an
  // administrator, a scheduler, or the person who currently IS the AIC (the
  // D-02 self-grab lock), while this page's edit rights are the broader
  // canManageAssignments. The rejection is deliberately NOT pre-empted with a
  // client-side guess at the rule — it is surfaced verbatim by the rail, so a
  // board manager reads the server's own refusal instead of a button that
  // looked like it worked.
  const makeAicFromRail = useCallback(
    async (row) => {
      const person = personForMdRow(row);
      if (!person) {
        // A sheet-only row (`mdcall:CODE`) is on no roster here, so there is no
        // person id to write. Thrown rather than ignored: a silent no-op looks
        // exactly like a successful handover.
        throw new Error(`${row?.name || "That doctor"} is not on today's roster here, so they cannot be made AIC.`);
      }
      await setDailyRoster(
        facilityId,
        date,
        { aicId: person.id, aicName: person.name || "" },
        user?.uid
      );
    },
    [personForMdRow, facilityId, date, user]
  );

  // ── HOW LONG HAS THIS PROVIDER BEEN FREE ────────────────────────────────
  //
  // `roster.freeSince` is a map of memberId -> the moment they came OUT of a
  // room, stamped server-side at the unassign (see setRosterMemberFreeSince).
  // Parsed defensively because Firestore hands the same value back in three
  // shapes depending on whether it round-tripped, came from cache, or was
  // serialised: Timestamp, {seconds}, or epoch ms.
  //
  // ⚠️ A MISSING ENTRY IS NOT ZERO. It means this provider was never in a room
  // today, and the card shows TBD. `|| 0` here would start every one of them at
  // "0m" and count up from page load — a number that looks authoritative, resets
  // on refresh, and disagrees between two devices watching the same board.
  const freeSince = useMemo(() => {
    const raw = roster?.freeSince;
    if (!raw || typeof raw !== "object") return {};
    const out = {};
    for (const [id, v] of Object.entries(raw)) {
      if (!v) continue;
      let ms = null;
      if (typeof v?.toDate === "function") ms = v.toDate().getTime();
      else if (typeof v?.seconds === "number") ms = v.seconds * 1000;
      else if (typeof v === "number") ms = v;
      if (Number.isFinite(ms)) out[id] = ms;
    }
    return out;
  }, [roster]);

  // The minute tick that drives the elapsed figures. `nowMin` already ticks
  // every 30s for the chart; deriving from it keeps one clock for the page so
  // the rail, the chart and these timers can never disagree.
  const nowMs = useMemo(() => Date.now(), [nowMin]);

  // ── ASSIGN — THE BOARD'S OWN CHOOSER ────────────────────────────────────
  //
  // `BenchAssignFlow`, the same portal modal BoardPage opens from its Unassigned
  // section (BoardPage:1461-1470). It owns Takeover, Home and Transfer itself
  // and delegates only New Room back to the parent, which is why the parent has
  // to mount `AssignmentModal` too — exactly as BoardPage does at :1521.
  //
  // Two earlier attempts at this button were wrong in the same way: first a
  // route I invented (`/daily?assign=`), then a DIFFERENT placement sheet. Both
  // looked reasonable. The rule that would have caught both: find the component
  // the working screen already renders and mount THAT.
  //
  // ⚠️ AND NOW A SECOND ENTRY POINT INTO THE SAME MOUNT: a NAME on a case bar.
  // `useBarNameMenu` resolves the bar's id + name into the provider object the
  // menu needs (roster shift code, sub-role, and the break-giver label that
  // lives on the roster and nowhere else), and remembers which row it was
  // opened from and which trigger focus goes back to.
  //
  // ⚠️ AND IT NEEDS THE STAFF MASTER AS WELL AS THE ROSTER. A name the importer
  // never matched (`ahpId: null` — Taylor Prentice, Kayla Gibbons, Josh Larson)
  // is resolved by NAME against this pool, and the roster alone is not enough:
  // the very people who lost their id are also the ones the importer failed to
  // enrol, so they are frequently in `staff` and not in `roster.crnas`. Passing
  // only `crnaPool` would leave them unidentified for the same reason twice.
  const nameMenu = useBarNameMenu({ crnaPool, staffPool: staffProviders || [], roster });
  const benchProvider = nameMenu.provider;
  const [showAssign, setShowAssign] = useState(false);
  const [assignProviderId, setAssignProviderId] = useState(null);
  // ONE AssignmentModal mount, two entry points — exactly BoardPage's shape.
  // The bench chooser hands back New Room and wants the SIMPLIFIED form (room,
  // times, CRNA, MD); MAINTENANCE wants the full one. A second mount would be a
  // second copy of eleven props to keep in step.
  const [simplifiedAssign, setSimplifiedAssign] = useState(false);
  // MAINTENANCE → "Add a provider": the board's CrossFacilityPicker, which is
  // the modal that asks CRNA vs Doctor.
  const [showAddProvider, setShowAddProvider] = useState(false);
  const { active: activeLocs } = useLocations(facilityId);

  // ── THE POOLS THE BOARD'S MODALS EXPECT ─────────────────────────────────
  //
  // ⚠️ ROSTER-GATED, NOT THE WHOLE STAFF MASTER. `AssignmentModal`'s dropdowns
  // are "who may be placed today", and BoardPage feeds them `rosterDoctors` /
  // `rosterProvidersAll` — the staff pool filtered to today's roster
  // (BoardPage:684,722). This page used to hand the modal the entire
  // `staffDoctors` / `staffProviders` collection, which offered people who are
  // not working today as if they were.
  //
  // Named `staffDoctorPool` / `staffProviderPool` deliberately: this file
  // ALREADY has a `doctorPool` and a `crnaPool` (above), and those are the
  // ROSTER MEMBER objects the relieve flow uses. Same word, different thing —
  // reusing the names would have silently swapped a roster list for a master
  // list in whichever flow was edited second.
  const staffDoctorPool = testingMode ? [...(staffDoctors || []), ...testDoctors()] : (staffDoctors || []);
  const rawProviderPool = testingMode ? [...(staffProviders || []), ...testProviders()] : (staffProviders || []);
  // A CRNA's per-day shift / leave time lives on the roster, not the staff
  // profile, and it has to TRAVEL when the modal reassigns them — the same
  // merge BoardPage:653-662 does.
  const crnaDayById = new Map((roster?.crnas || []).map((c) => [c.id, c]));
  const staffProviderPool = rawProviderPool.map((p) => {
    const day = crnaDayById.get(p.id);
    if (!day) return p;
    return {
      ...p,
      ...(day.shiftCode != null ? { shiftCode: day.shiftCode } : {}),
      ...(day.goHomeHour != null ? { goHomeHour: day.goHomeHour } : {}),
    };
  });
  const allowedDoctorIds = new Set(
    [roster?.aicId, ...(roster?.supervisors || []).map((srv) => srv.id)].filter(Boolean)
  );
  const allowedProviderIds = new Set((roster?.crnas || []).map((c) => c.id).filter(Boolean));
  const rosterDoctors = staffDoctorPool.filter((d) => allowedDoctorIds.has(d.id));
  const rosterProvidersAll = staffProviderPool.filter((p) => allowedProviderIds.has(p.id));

  // The modal refuses to build a row when nobody is rostered; it needs to know.
  // ⚠️ COUNTS THE AIC. This used to read `roster.supervisors.length === 0`, which
  // calls a board with an AIC and no other supervisor "empty" — BoardPage:665,723
  // puts `aicId` in the allowed set, and the modal's own "create the day's list
  // first" branch keys off this flag.
  const rosterEmpty = allowedDoctorIds.size === 0 && allowedProviderIds.size === 0;

  // Persist a supervising doctor's day call number when the Add Assignment modal
  // collects one. THE SAME WRITE BoardPage:633-639 performs — without it the
  // timeline's copy of the modal would take the call number, drop it, and leave
  // MD CALL disagreeing with the board about who is #2.
  //
  // A PLAIN FUNCTION, not a `useCallback` — same as BoardPage. The pools above
  // are rebuilt on every render, so a callback keyed on them would memoize
  // nothing while implying that it did (the note on `taskPeople` says the same).
  const handleSetCallOrder = async (attendingId, callOrder) => {
    const list = roster?.supervisors || [];
    const next = list.some((m) => m.id === attendingId)
      ? list.map((m) => (m.id === attendingId ? { ...m, callOrder } : m))
      : [
          ...list,
          { id: attendingId, name: staffDoctorPool.find((d) => d.id === attendingId)?.name || "", callOrder },
        ];
    await setDailyRoster(facilityId || "default", date, { supervisors: next }, user?.uid);
  };

  // ── MAINTENANCE, AND THE ONE DAY IT MAY NOT RUN ─────────────────────────
  //
  // ⚠️ `AssignmentModal` STAMPS ITS ROW WITH `rosterDateKey()` — TODAY —
  // regardless of the date it was opened over (AssignmentModal.js:162). On the
  // OR Board that is invisible: the board IS today. This page has a free date
  // picker, so the identical click while browsing another day would write a real
  // assignment onto today's LIVE board while the operator was looking at a
  // different one. Named rather than silently disabled, same as the task
  // composer's blocked reason above.
  const maintenanceBlockedReason = !isToday
    ? "You are viewing another day. Both of these act on TODAY's board, so switch the date back to today first."
    : null;

  // ── DRAGGING A BAR, AND CLICKING BESIDE ONE ─────────────────────────────
  //
  // ⚠️ THE SAME GATE THE THREE COMMAND BOARDS USE, not a fourth opinion. A board
  // that cannot prove it is live must not be acted upon, and someone previewing
  // another user is not that user.
  const writeGate = writesAllowed({
    canAct: canEditCases,
    connectionBand: connection.band,
    previewing: isPreviewing,
    // Testing mode swaps the whole data plane for a local store that is
    // destroyed on leaving, so neither the preview gate nor the live-connection
    // gate has anything to protect — and both were refusing the writes the
    // session exists to exercise.
    ephemeral: isTestModeBoot(),
  });
  const dragAllowed = writeGate.allowed;
  // ONE refusal, shared by every gesture on this chart. Two copies would drift.
  const writeGuard = () => {
    if (writeGate.allowed) return true;
    const why = WRITE_BLOCKED_COPY[writeGate.reason];
    if (why) toast.error(why);
    return false;
  };
  // Plain keys for the transfer callback's deps array — see the note there.
  const writeBlockedReason = writeGate.reason;

  // Sending a doctor to another campus. TWO writes, exactly as BenchAssignFlow
  // does it (:149-161) — the transfer doc is what the Traveling card tracks, and
  // the origin roster must mark them departed, or this board keeps counting them
  // as available while they are on the road.
  //
  // ⚠️ DECLARED HERE, BELOW `writeGuard`, NOT beside the other release state.
  // It sat above at first, and naming `writeGuard` in a useCallback dependency
  // array evaluates it AT RENDER — a temporal-dead-zone ReferenceError and a
  // blank page. The build compiles it happily and this page has no render test,
  // so nothing would have caught it before a real board did.
  const transferDoctor = useCallback(
    async (entry, dest) => {
      // The same refusal every other gesture on this chart uses — a board that
      // cannot prove it is live must not move a doctor between hospitals.
      if (!writeGuard()) return;
      setTransferBusyKey(entry.key);
      try {
        await createTransfer({
          providerId: entry.key,
          providerName: entry.name,
          // ⚠️ NOT the "crna" default BenchAssignFlow falls back to — this menu
          // only ever lists doctors, and the subRole rides through to the
          // destination's arrival card.
          subRole: "anesthesiologist",
          fromFacilityId: facilityId,
          toFacilityId: dest.id,
          toFacilityName: dest.label,
          date,
          createdBy: user?.uid ?? null,
        });
        await setRosterMemberDeparted(facilityId, date, entry.key, true, user?.uid ?? null);
        toast.success(`${entry.name} is traveling to ${dest.label} — Arrival confirms when they get there.`);
        setReleaseOpen(false);
      } catch (e) {
        console.error("Doctor transfer failed:", e);
        toast.error("Could not start the transfer.");
      } finally {
        setTransferBusyKey(null);
      }
    },
    // ⚠️ PLAIN KEYS ONLY, AND NO eslint-disable. CRA lints expressions in a
    // deps array, and an `eslint-disable` for react-hooks/exhaustive-deps is a
    // BUILD ERROR in this project because the rule is not configured — NavMenu
    // :190 already documents both traps and I walked into them anyway. The
    // guard's inputs are hoisted above so this array holds identifiers only.
    [facilityId, date, user, toast, dragAllowed, writeBlockedReason]
  );
  const caseCommit = useCaseCommit({
    sections: model.sections,
    placed: model.placed,
    facilityId,
    dateKey: date,
    realUser,
    staff: taskStaff,
    doctors: rosterDoctors,
    providers: rosterProvidersAll,
    toast,
    guard: writeGuard,
  });
  // ⚠️ THE SAME GATE, A DIFFERENT WRITE. The lower lane of a paired room moves
  // ONE PERSON'S leaving time (`orienteeDepartTime`); it collides with nothing,
  // changes no room's hours, and must not page the row the way a rescheduled
  // case does. Routing it through `caseCommit` would have written the room's
  // `startTime`/`endTime` from a gesture about somebody else.
  const secondCommit = useSecondDepartCommit({ toast, guard: writeGuard });
  // The ORIENTEES card's move-out. It takes the whole assignment list rather
  // than a row, because the patch is re-derived from the LIVE assignment at
  // click time — a card row is a snapshot of an earlier render.
  const movePreceptorOut = useMovePreceptorOut({ assignments, toast, guard: writeGuard });
  // The pair recorded the wrong way round. Same inputs as the move-out and a
  // separate hook, because it is a separate write: nobody leaves the room.
  const swapPair = useSwapPair({ assignments, toast, guard: writeGuard });

  // Clicking either NAME on an ORIENTEES row opens THAT ROOM'S OWN EDITOR.
  //
  // ⚠️ THE EDITOR ALREADY EXISTS AND ALREADY DOES THE HARD HALF. `TimelineCaseModal`
  // replaces the seat and replaces the second provider, and it also disposes the
  // person who left and stamps them free — writes a side panel cannot see. A
  // picker built into the card would have been a second implementation of that,
  // missing exactly the parts nobody remembers until a room reads as staffed
  // with nobody in it.
  //
  // ⚠️ `model.sections`, NOT `visibleSections`. A released room is gone from the
  // chart and not from the day; the ORIENTEES card is filtered by its own clock,
  // so if it is showing the row the case must be findable.
  const openPairEditor = useCallback(
    (cardRow) => {
      const id = cardRow?.assignmentId;
      if (!id) return;
      for (const sec of model.sections || []) {
        for (const room of sec.rooms || []) {
          const found = (room.cases || []).find((c) => c?.id === id);
          if (found) {
            setEditCase(found);
            return;
          }
        }
      }
      // ⚠️ SAYING SO BEATS A NAME THAT DOES NOTHING. The row is built from the
      // assignments stream and the chart from the model; they can disagree for a
      // render after an import.
      if (toast) toast.error("That room is not on the chart right now. Refresh and try again.");
    },
    [model.sections, toast]
  );
  const { laneCreate, onTrackPointerDown, onTrackClick, closeLaneCreate } = useLaneCreate({
    enabled: dragAllowed,
    axisStart,
    span,
    toast,
    // The date guard the maintenance card already carries, for the identical
    // reason: `AssignmentModal` writes `date: rosterDateKey()` whatever day the
    // page is showing.
    blockedReason: maintenanceBlockedReason,
  });

  const providersOnShift = useMemo(() => {
    const crnas = (roster?.crnas || []).filter((c) => c && c.id);
    const departed = roster?.departed || {};
    return crnas.filter((c) => !departed[c.id]).length;
  }, [roster]);

  return (
    <div style={s.page}>
      {/* TOPBAR */}
      <div style={s.topbar}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", minWidth: 0 }}>
          <span style={s.logo}>⚕ ATC</span>
          <NavMenu compact={isMobile} />
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {/* Jump straight to the wall-display board (user request 2026-07-08). */}
          <button
            style={s.tvModeBtn}
            onClick={() => navigate("/board?tv=1")}
            title="Open the OR board in full-screen TV mode"
          >
            📺 TV Mode
          </button>
          <Avatar user={user} isDoctor={isDoctor} onClick={() => navigate("/profile")} />
          <SignOutButton compact={isMobile} />
        </div>
      </div>

      <main style={s.main}>
        {/* 7-COLUMN PAGE GRID — the entire Timeline Chart (header, legend, the
            chart itself, staffing adequacy, other-facility summaries, locum
            list, and the click-to-edit modal it opens) sits in columns 2–4.
            Columns 1 and 5–7 are intentionally empty. The grid draws nothing:
            no rules, no borders, no background — only tracks.

            On a phone the seven tracks collapse to a single full-width column.
            Three sevenths of a 390px screen is 167px, which is not a Gantt
            chart, so the split only applies from tablet up.

            Interior indentation is left as-is on purpose: re-indenting ~300
            lines to add one wrapper would bury a layout change in a whitespace
            diff. */}
        <div style={wallMode ? { ...s.wallScaler, transform: `scale(${wallScale})`, width: `${100 / wallScale}%` } : undefined}>
        <div style={isStacked ? s.gridMobile : s.grid}>
          {/* COLUMN 1 — the status rail. The trust block sits ABOVE the counts
              so a "0" cannot be read without first crossing the line that says
              whether this screen is live and which campus it is showing. */}
          <div style={isStacked ? s.gridRailMobile : s.gridRail}>
            {/* ONE sticky wrapper for the whole column, and it is the ONLY
                sticky thing in it. Two sticky siblings pinning to the same
                offset would sit on top of each other, so `TimelineRail` no
                longer carries a sticky rule of its own — see the note on
                `s.rail` there. That rule used to be "harmless because the
                wrapper pins first", which was only true on this branch: when
                the layout stacks the wrapper is not applied at all, and the
                rail's own stickiness displaced it over the MD call card. */}
            <div style={isStacked ? undefined : s.railStack}>
            <TimelineRail
              engineState={engineState}
              canToggleEngine={isAdmin}
              engineBusy={engineBusy}
              onToggleEngine={toggleEngine}
              facilityName={facilityLabel}
              dateKey={date}
              clockLabel={to12h(nowMin)}
              connection={connection}
              alertsCovered={alertsCovered}
              alertHomeFacilityName={alertHomeFacilityName}
              doctorPlan={panel.doctorPlan}
              uncovered={uncoveredCount}
              uncoveredUnavailable={!alertsCovered}
              planned={!isToday}
              capacity={{
                roomsRunning: model.occupiedCount,
                // One source for this number. `railMetrics.roomsTotal` already
                // excludes the TBD placeholder (see useControlPanelData), and
                // the three boards read the same field — so the rail cannot
                // drift from them the way it did when it counted rooms itself.
                roomsBuilt: railMetrics?.roomsTotal ?? null,
              }}
              providers={{ onShift: providersOnShift, bench: available?.providers?.length || 0 }}
              canEditDoctors={doctorsGate.gateOpen}
              doctorsEditLockReason={doctorsGate.gateNote || ""}
              releaseOpen={releaseOpen}
              onExtraDoctor={() => setReleaseOpen((v) => !v)}
              releaseMenu={
                <DoctorReleaseMenu
                  open={releaseOpen}
                  onClose={() => setReleaseOpen(false)}
                  releasable={panel.doctorPlan?.releasable ?? 0}
                  doctors={panel.doctorPlan?.now?.doctorList || []}
                  solo={panel.doctorPlan?.now?.soloList || []}
                  holdNote={doctorHoldNote}
                  // ⚠️ THE SAME DepartureFlow THE MD CALL RAIL RENDERS. Releasing
                  // a doctor IS departing them, and departure already enforces
                  // room reassignment and the AIC handoff. A second write path
                  // here would be a second set of rules to keep correct.
                  //
                  // `doctorList` entries are the projection's { key, name }, and
                  // `personForMdRow` keys off a staff id — so a locum invented by
                  // the sheet under a synthetic id resolves to null and the row
                  // says so rather than offering a control that cannot work.
                  renderDepart={(entry) => renderDepart({ id: entry.key, name: entry.name })}
                  destinations={doctorDestinations}
                  onTransfer={transferDoctor}
                  transferBusyKey={transferBusyKey}
                />
              }
            />
            {/* WHO IS ON CALL IN OB, ABOVE MD CALL (product owner, 2026-08-17).
                It reads the board's EXISTING on-call assignment rows
                (`isOnCall` — see domain/coverage.js) rather than a new store,
                and its edit writes them back through the same
                updateAssignmentRow / createAssignment pair AssignmentModal
                uses. Placed above MD CALL because OB coverage is a rule the
                board enforces, and a call list is reference.

                ⚠️ `rooms` IS LOAD-BEARING, not decoration: it resolves the
                on-call room for the room-doc mirror, and it is the only way the
                empty state can name a room when there is no on-call row to read
                one from. */}
            <div style={s.railOnCall}>
            <TimelineOnCallCard
              assignments={assignments}
              rooms={rooms}
              // The SAME roster-gated CRNA pool AssignmentModal is fed, so this
              // cannot offer somebody who is not working today.
              providers={rosterProvidersAll}
              facilityId={facilityId}
              date={date}
              // One clock for the page — the mirror names whoever is on call at
              // the moment of the save.
              nowMin={nowMin}
              // The same permission every other write affordance on this page
              // is gated on.
              canManageAssignments={canManageAssignments}
            />
            </div>
            {/* The Daily List's MD Call, restyled to the rail. It sits UNDER
                the capacity block on purpose: the rail is ordered trust →
                alarms → reference, and a call list is reference. */}
            <div style={s.railMdCall}>
            <TimelineMdCallRail
              rows={railMdCall}
              facilityBoards={facilityBoards}
              facilities={allFacilities}
              departed={departedDoctors}
              // The SAME permission the right-hand card's ✕ Depart is gated on
              // (:1208 below) — the campus gate is an ADDITIONAL condition, not
              // a new right.
              canEditBoard={canManageAssignments}
              deviceFacility={deviceFacility}
              viewedFacilityId={facilityId}
              // An admin has no geofence boundaries (2026-08-17): they support
              // every campus and are rarely standing in the one they are fixing.
              // They still edit only the board on screen — see the rail's header.
              isAdmin={isAdmin}
              onMakeAic={makeAicFromRail}
              renderDepart={canManageAssignments ? renderDepart : null}
            />
            </div>
            </div>
          </div>

          <div style={isStacked ? s.gridContentMobile : s.gridContent}>
        {/* Header: title (left) · 🚗 Traveling (middle, only while someone is
            in transit — same data/actions as the board bench) · controls
            (right) — all on the SAME row. */}
        <div style={s.header}>
          <div>
            <h1 style={s.pageTitle}>Timeline Chart</h1>
            <div style={s.subtitle}>
              {visibleRoomCount} occupied room{visibleRoomCount === 1 ? "" : "s"} ·{" "}
              {model.caseCount} case{model.caseCount === 1 ? "" : "s"} · {facilityLabel}
            </div>
          </div>
          <div style={s.controls}>
            {facilities.length > 1 && (
              <div>
                <label style={s.label} htmlFor="tl-facility">Facility</label>
                <select
                  id="tl-facility"
                  style={s.select}
                  value={facilityId}
                  onChange={(e) => setFacilityId(e.target.value)}
                >
                  {facilities.map((f) => (
                    <option key={f.id} value={f.id}>{f.label}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label style={s.label} htmlFor="tl-date">Date</label>
              <input
                id="tl-date"
                type="date"
                style={{ ...s.select, width: 160 }}
                value={date}
                onChange={(e) => setDate(e.target.value || rosterDateKey())}
              />
            </div>
          </div>
        </div>

        {/* Section legend */}
        {visibleSections.length > 0 && (
          <div style={s.legend}>
            {visibleSections.map((sec) => (
              <span key={sec.key} style={s.legendItem}>
                <span style={{ ...s.legendSwatch, background: sec.tint.bg, borderColor: sec.tint.border }} />
                {sec.label}
              </span>
            ))}
          </div>
        )}

        {visibleRoomCount === 0 ? (
          <div style={s.empty}>
            No occupied rooms for {date}. Pick another date or facility, or build the
            day's schedule in Daily Assignments.
          </div>
        ) : (
          <>
          <div style={s.chart} ref={chartRef}>
            {/* Live current-time marker (today only). Positioned from the
                MEASURED track, not from the label column — see the note on
                `nowLeft` above. */}
            {showNow && (
              <div style={{ ...s.nowLine, left: nowLeft }} aria-hidden="true" data-testid="tl-now-line">
                <span style={s.nowLabel}>{to12h(nowMin)}</span>
              </div>
            )}

            {/* Axis row: hour labels across the top, aligned to the track. The
                spacer is the same measured offset the now-line uses, so the
                labels sit over the gridlines they name instead of ~64px left of
                them on an editable board. */}
            <div style={s.axisRow}>
              <div style={{ width: geom ? geom.offsetPx : leftW, flexShrink: 0 }} />
              <div style={s.axisTrack} ref={measureAxis}>
                {hourMarks.map((h, i) => {
                  // Thin out labels so they cannot collide.
                  //
                  // ⚠️ THIS USED TO BE `isMobile ? 3 : hourMarks.length > 12 ? 2 : 1`,
                  // which counts HOURS and ignores how much room they have. When
                  // the chart lost width to the 7-column split the same step
                  // stayed, and labels overprinted — "7 AM8 AM", "5 PM6 PM" — at
                  // every width from 834 to 1440. A label count is not a width.
                  //
                  // Now derived from the MEASURED track: keep at most one label
                  // per AXIS_LABEL_MIN_PX. Falls back to the old behaviour until
                  // the first measurement lands, so the first paint is never
                  // blank.
                  const keep = axisKeep.has(i);
                  if (!keep) return null;
                  const left = span > 0 ? ((h.min - axisStart) / span) * 100 : 0;
                  return (
                    <span
                      key={h.min}
                      style={{
                        ...s.axisTick,
                        ...(isMobile ? { fontSize: 9 } : {}),
                        left: `${left}%`,
                        transform: left >= 100 ? "translateX(-100%)" : left <= 0 ? "none" : "translateX(-50%)",
                      }}
                    >
                      {to12h(h.min)}
                    </span>
                  );
                })}
              </div>
            </div>

            {/* Section blocks */}
            {visibleSections.map((sec) => (
              <div key={sec.key} style={s.section}>
                {/* Compact section chip — sits at the left, does NOT stretch
                    across the timeline. */}
                <div style={s.sectionHeadRow}>
                  <span style={{ ...s.sectionHead, background: sec.tint.bg, color: sec.tint.head, borderColor: sec.tint.border }}>
                    {sec.label}
                  </span>
                </div>
                {sec.rooms.map((room) => {
                  // A LIVE EMERGENCY IN THIS ROOM. Stays until the alert is
                  // resolved — `emergencyRoomIds` is derived from the same
                  // `isLiveAlert` the boards use, so acknowledging (which is
                  // per-user) does NOT stop the blink; only resolving does.
                  const inEmergency = Boolean(emergencyRooms?.has?.(room.id));
                  return (
                  <div key={room.id} style={{ ...s.row, height: roomRowHeight(room.cases.map(barCase), isMobile) }}>
                    <div
                      style={{ ...s.roomLabel, width: leftW, ...(inEmergency ? s.roomLabelEmergency : null) }}
                      title={inEmergency ? `${room.name} — live emergency` : room.name}
                      data-emergency={inEmergency ? "true" : undefined}
                    >
                      {/* Colour and motion are not the only carriers. The glyph
                          survives greyscale and a colour-blind reader, and the
                          hidden text is what a screen reader announces — the
                          blink is invisible to it. */}
                      {inEmergency && <span aria-hidden="true">🚨 </span>}
                      <span style={s.roomLabelName}>{room.name}</span>
                      {inEmergency && <span style={s.srOnly}> — live emergency</span>}
                      {/* ⚠️ INLINE, BECAUSE THE ROW MAY NOT GROW. A single row is
                          32px (SINGLE_ROW_H) and caseLanes says so in as many
                          words, so a second line would be clipped rather than
                          stacked.

                          ⚠️ AND THE PHASE IS THE PART THAT SHRINKS. The room
                          name is how a reader FINDS the row under pressure;
                          anything added to a one-line rail comes out of the only
                          shrinkable thing in it, so the name is pinned
                          (flexShrink 0) and the phase carries the ellipsis. */}
                      {(() => {
                        const ph = phaseForRoom(room.id);
                        return ph ? <span style={s.roomLabelPhase}>{ph}</span> : null;
                      })()}
                    </div>

                    {/* The board's two room controls, on the row that already
                        shows this room's people. Both MOUNT WHAT ALREADY WORKS
                        rather than repeating it: ⇄ opens this page's tested
                        relieve flow (TimelineCaseModal, which also disposes the
                        relieved CRNA to Home / Unassigned / Travel), and ➜
                        opens the very NextRoomModal the OR Board mounts.

                        Only for a room with a case to act on, and only for
                        someone who may manage assignments — a wall display and
                        an observer get the chart with no controls. */}
                    {canEditCases && room.cases.length > 0 && (
                      <div style={s.rowBtns}>
                        <button
                          type="button"
                          style={s.rowBtn}
                          aria-label={`Assign relief for ${room.name}`}
                          title="Assign relief — replace the CRNA and send them home, to the bench, or travelling"
                          onClick={() => setEditCase(room.cases[0])}
                        >
                          ⇄
                        </button>
                        <button
                          type="button"
                          style={s.rowBtn}
                          aria-label={`Set next room for ${room.name}`}
                          title="Next room — route this team to its destination"
                          onClick={() => setNextRoomFor(room)}
                        >
                          ➜
                        </button>
                      </div>
                    )}
                    {/* THE LANE. Clicking bare lane space proposes a new case
                        in this room at the minute clicked — `useLaneCreate`
                        carries every guard that keeps an accidental one from
                        paging phones. `data-tl-track` is how the drag and the
                        now-line find something real to measure. */}
                    {/* A DRAG SURFACE. Pointer-down begins drag-to-create; there is no keyboard
                        gesture for 'drag from 09:15 to 10:40', and inventing one on a div would
                        be worse than the real keyboard path, which is the Add-case button that
                        opens the same modal with the same guards. */}
                    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                    <div
                      data-tl-track=""
                      style={{ ...s.track, backgroundImage: gridBg, ...(dragAllowed ? { cursor: "copy" } : null) }}
                      onPointerDown={dragAllowed ? onTrackPointerDown : undefined}
                      onClick={dragAllowed ? (e) => onTrackClick(e, room) : undefined}
                    >
                      {/* ⚠️ `barCase(c)` DRAWS the case without its orientee, but
                          `onOpen` hands back the ORIGINAL `c`. The modal is still
                          where a second provider is edited, and passing it the
                          stripped case would quietly hide them from the one
                          screen that can change them. */}
                      {room.cases.map((c) => (
                        <CaseBar
                          key={c.id}
                          c={barCase(c)}
                          roomName={room.name}
                          tint={sec.tint}
                          isMobile={isMobile}
                          axisStart={axisStart}
                          span={span}
                          dragAllowed={dragAllowed}
                          canOpen={canEditCases}
                          trackWidth={geom ? geom.trackWidth : 0}
                          onOpen={() => setEditCase(c)}
                          onCommit={caseCommit.commit}
                          onSecondCommit={secondCommit.commit}
                          // A wall display and an observer get names that are
                          // text, not controls — `canEditCases` is the same gate
                          // the row's ⇄ / ➜ buttons use.
                          onNameMenu={canEditCases ? nameMenu.openForName : undefined}
                          openMenuFor={nameMenu.openFor}
                        />
                      ))}
                    </div>
                  </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Staffing adequacy — ONE card per shift boundary (3/5/7/9 PM),
              shift-coloured, with a shared "Rooms: N" header and TWO columns
              inside: Providers (CRNAs staying, with departures) | Doctors
              (need = rooms ÷ 4 rounded up vs available). Both columns derive
              from the same live timeline model, so any case add / move /
              reassign recomputes them instantly. */}
          <div style={s.staffWrap}>
            <div style={s.staffTitle}>
              Staffing adequacy
              <span style={s.staffNote}> — providers staying + doctors (1 per {ROOMS_PER_DOCTOR} rooms) vs. rooms running (−15m buffer)</span>
            </div>
            <div style={s.staffGrid}>
              {model.staffing.map((b, i) => {
                const doc = model.doctorStaffing[i] || {};
                const crnaMeta = STAFF_STATUS[b.status];
                const docMeta = STAFF_STATUS[doc.status] || crnaMeta;
                // Shift colour scheme: 3 PM amber · 5 PM blue · 7 PM violet · 9 PM red.
                const shiftColor = SHIFT_LABEL_COLORS[Math.floor(b.boundaryMin / 60)] || color.text.muted;

                // ── THE COVERAGE MAP'S ONE JOB ────────────────────────────
                // Each boundary card sits at the START of a block (3 PM opens
                // the 3–5 PM block), so it reads that block's configured room
                // count and compares it to the providers on shift through it.
                // `unknown` means this facility has no coverage map — fall back
                // to the original rooms-running maths rather than inventing a
                // requirement of 0, which would render as a confident
                // "Adequate" derived from no information at all.
                const covBlock = blockStartingAt(b.boundaryMin);
                const needed = covBlock ? coverageFor(facilityDoc, date, covBlock.id) : null;
                const adq = adequacy(b.crnas, needed);
                const covMeta = COVERAGE_STATUS[adq.status];

                return (
                  <div key={b.boundaryMin} style={{ ...s.staffCard, borderLeftColor: shiftColor, borderTopColor: shiftColor, background: `${shiftColor}0d` }}>
                    {/* Shared header: the shift, the rooms actually running, and
                        — when configured — the rooms this campus is meant to
                        cover in this block. */}
                    <div style={s.staffCardHead}>
                      <span style={{ ...s.staffTime, color: shiftColor }}>{b.label}</span>
                      <span style={s.staffRooms}>
                        Rooms: <strong>{b.rooms}</strong>
                        {needed != null && (
                          <> · Coverage: <strong data-testid="coverage-needed">{needed}</strong></>
                        )}
                      </span>
                    </div>
                    <div style={{ ...s.staffCols, flexDirection: isMobile ? "column" : "row" }}>
                      {/* LEFT — Providers (CRNAs staying past the boundary). */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={s.staffColTitle}>Providers</div>
                        {/* Verdict against the COVERAGE MAP when there is one,
                            otherwise the long-standing rooms-running verdict.
                            The wording comes from adequacyLabel() so this and
                            any future surface cannot drift apart. */}
                        {adq.status === "unknown" ? (
                          <div style={{ ...s.staffDelta, color: crnaMeta.color }}>{crnaMeta.label(b.delta)}</div>
                        ) : (
                          <div
                            style={{ ...s.staffDelta, color: covMeta.color }}
                            data-testid="coverage-verdict"
                            data-status={adq.status}
                          >
                            {adequacyLabel(adq)}
                          </div>
                        )}
                        <div style={s.staffMetrics}>
                          <span style={s.staffMetric}><strong>{b.crnas}</strong> staying</span>
                          {needed != null && (
                            <>
                              <span style={s.staffDot}>·</span>
                              <span style={s.staffMetric}>needs <strong>{needed}</strong></span>
                            </>
                          )}
                        </div>
                        <div style={s.staffNames}>
                          {b.crnaList.length === 0 ? (
                            <span style={s.staffNone}>none staying past {b.label}</span>
                          ) : (
                            /* ⚠️ THE `↳ orientee` LINE THAT USED TO BE HERE IS
                                GONE, AND MUST NOT COME BACK. It drew the second
                                CRNA as a subordinate of the first, and its
                                tooltip said they were "present, but not counted
                                as cover". Both halves are now false: the owner
                                decided two CRNAs in a room are two CRNAs
                                (`countsAsCover`, 2026-08-20), so each of them is
                                their OWN entry in this list, and he then removed
                                the hierarchy from the display entirely — *"i
                                dont care who is orienting who, place the 2
                                crnas."* `buildStaffing` no longer publishes
                                `orienteeName` on a pool entry at all, so the
                                branch rendered nothing — and a dead branch that
                                used to draw a hierarchy is exactly what somebody
                                re-enables later while "fixing" a blank line. */
                            b.crnaList.map((c) => (
                              <span
                                key={c.key}
                                style={{ ...s.staffName, color: shiftLabelColor(c.depart12h), ...(c.leavingSoon ? s.staffNameSoon : {}) }}
                                title={`${c.unassigned ? "Unassigned (bench) · " : ""}${c.leavingSoon ? `Leaving ${c.depart12h} — imminent` : `Leaves ${c.depart12h}`}`}
                              >
                                {c.name} <span style={s.staffNameTime}>{c.depart12h}</span>
                                {c.unassigned && <span style={s.staffBench}> · bench</span>}
                                {c.leavingSoon && <span aria-hidden="true"> ⚠</span>}
                              </span>
                            ))
                          )}
                        </div>
                      </div>
                      {/* RIGHT — Doctors, split into Supervision Available vs
                          Solo. Solo rooms are subtracted from the room count
                          before the ÷4 (a solo MD's room needs no supervisor,
                          and that MD can't supervise while the case runs).
                          When a solo case ends, the MD returns to the
                          Supervision pool at later boundaries automatically. */}
                      <div style={{ flex: 1, minWidth: 0, borderLeft: isMobile ? "none" : `1px solid ${color.border.default}`, borderTop: isMobile ? `1px solid ${color.border.default}` : "none", paddingLeft: isMobile ? 0 : 10, paddingTop: isMobile ? 8 : 0 }}>
                        <div style={s.staffColTitle}>Doctors — supervision</div>
                        <div style={{ ...s.staffDelta, color: docMeta.color }}>{docMeta.label(doc.delta)}</div>
                        <div style={s.staffMetrics}>
                          <span style={s.staffMetric}>need <strong>{doc.required}</strong></span>
                          <span style={s.staffDot}>·</span>
                          <span style={s.staffMetric}>have <strong>{doc.doctors}</strong></span>
                          {doc.soloRooms > 0 && (
                            <>
                              <span style={s.staffDot}>·</span>
                              <span style={s.staffMetric} title={`${doc.rooms} rooms − ${doc.soloRooms} solo = ${doc.supervisionRooms} needing supervision`}>
                                <strong>{doc.soloRooms}</strong> solo excluded
                              </span>
                            </>
                          )}
                        </div>
                        <div style={s.staffNames}>
                          {(doc.doctorList || []).length === 0 ? (
                            <span style={s.staffNone}>no doctors available to supervise</span>
                          ) : (
                            doc.doctorList.map((d) => (
                              <span key={d.key} style={s.staffName}>{d.name}</span>
                            ))
                          )}
                        </div>
                        {(doc.soloList || []).length > 0 && (
                          <>
                            <div style={{ ...s.staffColTitle, marginTop: 6 }}>Solo</div>
                            <div style={s.staffNames}>
                              {doc.soloList.map((d) => (
                                <span key={d.key} style={{ ...s.staffName, color: "#b45309" }}>
                                  {d.name} <span style={s.soloTag}>SOLO</span>
                                </span>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Condensed staffing for the OTHER two facilities — label + the
              four shift boxes, numbers only (rooms / CRNA±spare / doctor±).
              Each runs its own live listeners, so it tracks that campus's
              board in real time. */}
          {/* ⚠️ SCOPED TO WHAT THIS ACCOUNT CAN READ. `otherFacilities()` returns
              a hardcoded tenant list and does NOT consult `user.facilityIds`, so
              this used to open rooms + assignments listeners on campuses the
              rules deny — two of the four `permission-denied` snapshot errors
              this page logged for a plain doctor. Each summary then rendered
              zeros, which on a staffing panel is indistinguishable from a campus
              that is genuinely closed. Filtering to facilities the account can
              actually read removes both the noise and the false zeros. */}
          {otherFacilities(facilityId)
            .filter((f) => canBrowseAllFacilities || myFacilityIds.includes(f.id))
            .map((f) => (
              <FacilityStaffingSummary key={f.id} facilityId={f.id} label={f.label} date={date} />
            ))}

          </>
        )}
          </div>

          {/* COLUMNS 5-7 — ordered by the question a clinician actually asks, in
              the order they ask it: is anyone in trouble, who can I send (the
              answer to the first, hence adjacent), am I staffed for the next
              boundary, who do I ring. */}
          <div style={isStacked ? s.gridSideMobile : s.gridSide}>
            <TimelineSidePanels
              // ⚠️ A NODE, like `taskCard` and `travelingBox`: the page owns the
              // writes and the panel only places it. It renders null on the
              // ordinary day where no room is in doubt, so the prop costs the
              // rail no space when there is nothing to ask.
              finishedRoomsCard={
                <FinishedRoomsCard
                  rows={finishedRoomRows}
                  onClose={closeRoom}
                  onKeep={keepOpen}
                  busyId={closingRoomId}
                />
              }
              outstanding={outstanding}
              alertsCovered={alertsCovered}
              checkedAtLabel={isToday ? to12h(nowMin) : null}
              available={available}
              boundary={boundary}
              facilityLabel={facilityLabel}
              weekdayLabel={weekdayKey(date)?.toUpperCase()}
              mdCall={mdCall}
              // Passed as a NODE rather than as props, so the page keeps owning
              // the transfer listener and the panel stays presentational.
              // Locum leaving times, at the FOOT of the right stack — directly
              // under the MD Call List it belongs to. It sat below the staffing
              // cards, a full page-scroll away from the call list whose people
              // it is about. LocumList renders null when there are no locums.
              // Same contract as the maintenance slot above: LocumList renders
              // null on a day with no locums, so the PROP has to be absent too,
              // or the panel builds a full-width cell around nothing.
              locumList={locums.length > 0 && (
                <LocumList
                  locums={locums}
                  leaveTimes={roster?.locumLeaveTimes || {}}
                  date={date}
                  uid={user?.uid}
                  canEdit={canEditCases}
                  nowMin={isToday ? nowMin : null}
                />
              )}
              travelingBox={
                /* ⚠️ WRAPPED IN A ROW ON PURPOSE. TravelingBox's root is
                   `flex: 0 1 560px` (TravelingBox.js:125), written for the page
                   header, which is a ROW — there the 560px basis means WIDTH.
                   Dropped straight into the side stack, which is a COLUMN, the
                   main axis flips and the same basis becomes a 560px HEIGHT: a
                   yellow box five times taller than its content, measured at
                   555px. This wrapper restores a row context so the basis means
                   width again and `0 1` lets it shrink into the column.
                   Fixed here rather than in TravelingBox, which is still correct
                   for the pages that render it in a row. */
                <div style={{ display: "flex" }}>
                  <TravelingBox facilityId={facilityId} date={date} uid={user?.uid} canManage={canEditCases} />
                </div>
              }
              departedDoctors={departedDoctors}
              freeSince={freeSince}
              nowMs={nowMs}
              canAssign={canManageAssignments}
              // Assignment happens where it already lives — the board's own
              // assign flow — rather than in a second placement path on a
              // summary panel.
              onAssign={(p) => nameMenu.openForBenchProvider(p)}
              // Departing reassigns rooms and can force an AIC handoff, so it is
              // offered only to the people the page already trusts to edit a
              // case. A read-only viewer sees the list without the action.
              renderDepart={canManageAssignments ? renderDepart : null}
              status={statusCounts}
              // Sounding the all-clear. Only rows whose `canClear` is true render
              // the control; this is the write behind it.
              onClearEmergency={resolveAlertById}
              onConfirmRelief={canManageAssignments ? onConfirmRelief : null}
              busy={confirmingRelief}
              breakRows={breakRows}
              orienteeRows={orienteeRows}
              // Moving a preceptor out changes who holds a room, so it is offered
              // only to the people already trusted to edit a case. A read-only
              // viewer sees both names and no control.
              onMovePreceptorOut={canManageAssignments ? movePreceptorOut.commit : null}
              onSwapPair={canManageAssignments ? swapPair.commit : null}
              onEditPair={canEditCases ? openPairEditor : null}
              // ⚠️ `claimMin`, NOT `nowMin` — null on any day that is not
              // today, so a planned board never claims anybody is overdue.
              nowMin={claimMin}
              renderSendHome={canManageAssignments ? renderSendHome : null}
              // Break state is CHANGED where it already has a tested home: the
              // room's own break flow. This panel navigates there rather than
              // opening a second write path onto the same clinical action.
              onOpenRoom={(row) => navigate(`/board?room=${encodeURIComponent(row.roomId || "")}`)}
              onAssignBreak={canManageAssignments ? onAssignBreak : null}
              onCancelRequest={canManageAssignments ? onCancelRequest : null}
              onClearBreak={canManageAssignments ? onClearBreak : null}
              // FIX — the placement screen behind an actionable row. Gated on
              // the same permission the relieve/assign flows use: it writes
              // assignment rows and room docs, which a read-only viewer may not.
              canFix={canManageAssignments}
              onFix={(item) => setFixItem(item)}
              // TASK DIRECTIVES rides in the panel's own reference row, stacked
              // under MD CALL LIST — the empty space beside BREAKS that it was
              // asked to fill. Passed as a NODE (like `locumList`/`travelingBox`)
              // so this page keeps owning the data and the panel only places it.
              taskCard={
      <TimelineTaskCard
                      tasks={taskDirectives}
                      loading={taskDirectivesLoading}
                      people={taskPeople}
                      staff={taskStaff}
                      facilityId={facilityId}
                      dateKey={date}
                      senderId={user?.uid}
                      senderName={user?.name || ""}
                      canCompose={!composeBlockedReason}
                      composeBlockedReason={composeBlockedReason}
                      // The day's OR rooms, so the place control offers real
                      // names. `rooms` is the same list the board renders.
                      rooms={rooms}
                      // Campuses for the one verb whose place is a building,
                      // not a room. Offering ORs there would let an operator
                      // send somebody to "WOR 5" twenty-five minutes away.
                      campuses={facilities}
                      // Where the traveller is coming FROM, for the far end's
                      // half of a transfer.
                      originName={facilityLabel}
                    />
              }
              // MAINTENANCE — stacked under BOUNDARY OUTLOOK, in its track.
              // A LAUNCHER ONLY: both buttons open the OR Board's own modals,
              // mounted below outside the wall-mode transform. Passed as a NODE
              // like the other three slots, so the panel places it and this page
              // keeps owning the modals and their props.
              // ⚠️ GATED HERE, NOT INSIDE THE CARD. TimelineMaintenanceCard
              // returns null for anyone who cannot manage assignments — most
              // CRNAs and AAs — but the PROP was still a truthy element, and
              // the panel decides whether to build the stacked wrapper from the
              // prop's truthiness. So every ordinary viewer got
              // `<div cellStack><BoundaryOutlook/><div cellFill/></div>`: an
              // empty filler with `flex: 1`, stretched to match the tall BREAKS
              // card beside it. That is the exact empty-space hole this panel
              // was rebuilt to close, reopened for the majority of the people
              // who open the page — and worse than before, because the old
              // plain column let an empty child contribute 0px.
              // An unrenderable card must be an ABSENT prop, the same way
              // `renderDepart`, `canAssign` and `canFix` are gated on this page.
              maintenanceCard={canManageAssignments && (
                <TimelineMaintenanceCard
                  canManage={canManageAssignments}
                  blockedReason={maintenanceBlockedReason}
                  // ⚠️ A LAUNCHER, LIKE THE OTHER TWO — it opens the flow that
                  // already exists rather than growing a second one here. A
                  // room needs a NAME and a LOCATION, and the location list is
                  // per-facility; a bare "new room" button on a 179px card
                  // would have to guess it, and a room filed under the wrong
                  // location is invisible on the board it belongs to.
                  onAddProvider={() => setShowAddProvider(true)}
                  onAddAssignment={() => {
                    // The FULL form — no preset person, no simplified branch.
                    setAssignProviderId(null);
                    setSimplifiedAssign(false);
                    setShowAssign(true);
                  }}
                />
              )}
            />

          </div>
        </div>
        </div>
      </main>

      {/* The bench chooser: New Room / Takeover / Home / Transfer. Portals to
          the body, so the wall-mode transform on <main> cannot trap it. */}
      <BenchAssignFlow
        provider={benchProvider}
        rooms={rooms}
        assignments={assignments}
        facilityId={facilityId}
        date={date}
        updatedBy={user?.uid}
        // Today's roster — the pool "Assign Supervising CRNA" picks from.
        crnaPool={crnaPool}
        // The row the menu was opened FROM when it was opened from a bar. That
        // is what tells the menu this person is already in a room; without it a
        // CRNA standing in OR 9 is offered "New Room" as if they were free.
        currentRow={nameMenu.currentRow}
        // ⚠️ THE SAME CLOCK FREE NOW USES. Without it the menu matched any row
        // for this person today, so a CRNA the board had already released from
        // a finished case was offered "put a second CRNA in their room" and
        // "set when they leave" for a case that ended hours earlier. `claimMin`
        // is already null on a board that is not today, so a past or future day
        // never times a row out.
        nowMin={claimMin}
        returnFocusRef={nameMenu.returnFocusRef}
        onNewRoom={(pid) => { nameMenu.close(); setAssignProviderId(pid); setSimplifiedAssign(true); setShowAssign(true); }}
        onClose={() => nameMenu.close()}
      />

      {/* ＋ Add a provider (MAINTENANCE) — the board's own CrossFacilityPicker,
          the modal that asks CRNA vs Doctor and then appends the person to this
          day's roster. Mounted here, a sibling of the page's other modals and
          OUTSIDE the wall-mode `wallScaler`: a transformed ancestor becomes the
          containing block for `position: fixed`, which would trap the dialog
          inside the chart. (Modal itself portals to <body>, so this is belt and
          braces — and the placement is what keeps it true if that ever
          changes.) Same props BoardPage:1516-1525 passes. */}
      <CrossFacilityPicker
        isOpen={showAddProvider}
        onClose={() => setShowAddProvider(false)}
        currentFacilityId={facilityId}
        date={date}
        actor={realUser}
        roster={roster}
        localPool={[...staffDoctorPool, ...staffProviderPool]}
        floatAllowed={facilityRules.floatAllowed || isAdmin}
      />

      {/* ＋ Add assignment (MAINTENANCE) — the full board modal — AND the New
          Room branch the bench chooser hands back, which wants the simplified
          form with the CRNA pre-filled. ONE mount, `simplified` carried in
          state, exactly as BoardPage:1528-1548 does it.

          ⚠️ `mustNotifyAIC` and `onSetCallOrder` are NOT optional decoration.
          Without the first the modal skips the AIC notification a supervising
          doctor's change is supposed to fire; without the second it collects a
          doctor's day call number and throws it away. Both are the same values /
          the same write BoardPage passes. */}
      {(showAssign || laneCreate) && (
        <AssignmentModal
          presetProviderId={assignProviderId}
          simplified={simplifiedAssign}
          {...(laneCreate
            ? {
                // ⚠️ ONE MOUNT, THREE ENTRY POINTS. A third dialog for "create a
                // case here" would be the bug, not the feature — the room, the
                // hours and `forceNewCase` are all this modal needed to answer an
                // empty-lane click, and `forceNewCase` is what stops that click
                // silently overwriting the room's morning case.
                presetRoomId: laneCreate.roomId,
                presetStartTime: laneCreate.startTime,
                presetEndTime: laneCreate.endTime,
                forceNewCase: true,
              }
            : null)}
          onClose={() => { setShowAssign(false); setAssignProviderId(null); setSimplifiedAssign(false); closeLaneCreate(); }}
          rooms={rooms}
          locations={locations}
          activeLocs={activeLocs}
          rosterEmpty={rosterEmpty}
          doctors={rosterDoctors}
          providers={rosterProvidersAll}
          assignments={assignments}
          facilityId={facilityId}
          mustNotifyAIC={mustNotifyAIC}
          realUser={realUser}
          onCreateList={() => navigate("/daily-list")}
          onSetCallOrder={handleSetCallOrder}
        />
      )}

      {/* Next room — the SAME modal the OR Board mounts, so the routing rules
          and the "Next Room / Coming from" chain cannot drift between the two
          surfaces. */}
      {nextRoomFor && canEditCases && (
        <NextRoomModal
          isOpen
          onClose={() => setNextRoomFor(null)}
          room={nextRoomFor}
          assignment={assignments.find((a) => a.orRoom === nextRoomFor.name) || null}
          rooms={rooms}
          assignments={assignments}
        />
      )}

      {/* "Fix" on a NEEDS YOU NOW row → place people into the affected rooms.
          The pools are the SAME two the relieve flow uses, so a person offered
          here is a person the board already knows about; `lanes` supplies the
          assignment rows to write and the windows the eligibility check runs
          over. */}
      {fixItem && canEditCases && (
        <OutstandingFixModal
          item={fixItem}
          lanes={lanes}
          uncoveredRoomIds={alarms?.uncoveredRoomIds}
          missingMdRooms={missingMdRooms}
          crnaPool={crnaPool}
          doctorPool={doctorPool}
          onClose={() => setFixItem(null)}
        />
      )}

      {/* A dragged bar's live announcement and its undo window. ⚠️ THE PHONE
          MESSAGE IS HELD BEHIND THIS BAR: nobody is told until the window closes,
          so an undone drag never pages anyone. */}
      <CaseDragStatus
        announcement={caseCommit.announcement}
        pending={caseCommit.pending}
        onUndo={caseCommit.undo}
      />

      {/* Click a case bar → edit its times or relieve the CRNA / doctor. */}
      {editCase && canEditCases && (
        <TimelineCaseModal
          caseItem={editCase}
          crnaPool={crnaPool}
          doctorPool={doctorPool}
          facilityId={facilityId}
          date={date}
          uid={user?.uid}
          surgeons={surgeons}
          onClose={() => setEditCase(null)}
        />
      )}
    </div>
  );
}

// Coverage-map verdict → colour. Overcovered is good news and reads green;
// adequate is deliberately NEUTRAL rather than green, so "we are exactly on
// target" does not compete for attention with a real shortfall; undercovered
// earns red because it is the one state on this card that needs somebody to act.
//
// Note this is the opposite call from the room-ceiling line, which never used
// colour at all: running extra ROOMS is routine, whereas being short of PEOPLE
// at a shift change is the thing this page exists to surface.
// ⚠️ THESE ARE 18px HEADLINES ON A LIGHT CARD AND MUST MEET AA (4.5:1).
//
// The accent tokens are chosen to be legible as BORDERS and FILLS, not as text
// on a near-white ground. Used directly as ink they fail, and the failures were
// measured on the live page:
//   accent.active  #10b981 on the card →  2.54:1   ("+2 spare")
//   accent.closing #f59e0b on the card →  2.15:1   ("Just enough")
// "Just enough" is one step from "Short" in meaning and was the least legible
// string on the page. The darkened inks below are the same hues at AA weight —
// they are the exact values `tokens.color.status.*.text` already uses for this
// purpose elsewhere, so this is a consistency fix as much as a contrast one.
const INK = {
  ok: "#096e53",    // 5.6:1 — matches status.active.text
  warn: "#7a4f00",  // 7.0:1 — matches status.closing.text
  bad: "#991b1b",   // 7.6:1 — accent.error is 4.41:1 as ink and fails AA
};

const COVERAGE_STATUS = {
  over: { color: INK.ok },
  adequate: { color: color.text.primary },
  under: { color: INK.bad },
  unknown: { color: color.text.muted },
};

// Staffing status → colour + headline. surplus=green, tight=amber, short=red.
// Still used as the FALLBACK for a facility with no coverage map configured.
const STAFF_STATUS = {
  surplus: { color: INK.ok,   bg: "#10b98112", label: (d) => `+${d} spare` },
  tight:   { color: INK.warn, bg: "#f59e0b16", label: () => "Just enough" },
  short:   { color: INK.bad,  bg: "#dc262610", label: (d) => `Short ${Math.abs(d)}` },
};

const s = {
  page: { background: color.surface.base, color: color.text.primary, minHeight: "100vh", fontFamily: font.family, display: "flex", flexDirection: "column" },
  topbar: { background: color.surface.card, borderBottom: `1px solid ${color.border.default}`, padding: "0 20px", height: 54, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 },
  tvModeBtn: { background: color.surface.raised, border: `1px solid ${color.border.default}`, color: color.text.secondary, borderRadius: radius.sm, padding: "7px 12px", fontSize: 12, fontWeight: font.weight.bold, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" },
  logo: { fontSize: 17, fontWeight: font.weight.bold, color: color.accent.primary },
  main: { flex: 1, overflowY: "auto", padding: "24px 22px 40px" },
  // ── 7-column page grid ────────────────────────────────────────────────
  // Seven equal tracks. Content spans 2–4; 1 and 5–7 are left empty. The grid
  // is structure only — no `border`, no `background`, no divider rules — so
  // nothing of the layout itself is ever visible.
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    columnGap: 24,
    alignItems: "start",
    border: "none",
    background: "transparent",
  },
  // `minWidth: 0` is load-bearing. A grid item's default `min-width: auto`
  // lets its content set the floor, and the chart's percent-positioned bars
  // plus the fixed 152px room-label column would push this track wider than
  // its 3/7 share, silently stealing width from the empty columns.
  gridContent: { gridColumn: "2 / 5", minWidth: 0 },
  gridRail: { gridColumn: "1 / 2", minWidth: 0 },
  // The rail column travels with the page as one unit. See the note at the
  // wrapper: two sticky siblings sharing an offset would sit on top of another.
  //
  // ⚠️ BLOCK, NOT FLEX. `TimelineRail`'s own root sets `alignSelf: "start"`
  // (harmless in its grid cell, where it just stops the rail stretching to the
  // chart's full height). Inside a flex COLUMN that same declaration is
  // cross-axis, so the rail shrank to the width of its widest chip while the MD
  // call card below it kept the full track — the two cards visibly disagreed on
  // where the column ended. In block layout `alignSelf` is inert and both fill
  // the track. Gap is carried by the child's margin for the same reason.
  railStack: { position: "sticky", top: 12, minWidth: 0 },
  // Gap carried by the child's margin, not a flex `gap` — the rail column is
  // BLOCK layout on purpose (see railStack above).
  railOnCall: { marginTop: 8 },
  railMdCall: { marginTop: 8 },
  gridSide: { gridColumn: "5 / 8", minWidth: 0 },
  // Phone: one full-width column, stacked in urgency order — the side panels
  // (what needs you now) come BEFORE the Gantt, because on a 390px screen the
  // chart is a reference document and the worklist is the reason you opened the
  // page. A 3/7 slice of 390px is 167px, so the 7-track split does not apply.
  gridMobile: { display: "flex", flexDirection: "column", gap: 14 },
  gridContentMobile: { minWidth: 0, order: 3 },
  gridRailMobile: { minWidth: 0, order: 1 },
  // ⚠️ ONE TRANSFORM, NOT A HUNDRED EDITED FONT SIZES. Every size on this page
  // is a raw px number in an inline style object; scaling them individually
  // would be ~150 edits and would drift the moment someone adds a card. A single
  // `scale()` on the grid keeps every proportion — type, bars, gaps, the
  // percent-positioned Gantt — exactly as designed, just larger.
  //
  // `width: 100/k %` compensates so the scaled box still fills the column: a
  // transform does not change layout size, so without it the page would render
  // at k× inside a 1× footprint and clip.
  //
  // ⚠️ THE MODAL MUST STAY OUTSIDE THIS BOX. A transformed ancestor becomes the
  // containing block for `position: fixed` descendants, which would trap the
  // case modal inside the chart instead of centring it on the viewport. It is
  // rendered as a sibling of <main>, after it, and must remain there.
  wallScaler: { transformOrigin: "top left" },
  gridSideMobile: { minWidth: 0, order: 2 },
  header: { display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 20, flexWrap: "wrap", marginBottom: 14 },
  pageTitle: { fontSize: font.size.display, fontWeight: font.weight.bold, margin: "0 0 4px", lineHeight: font.lineHeight.tight },
  subtitle: { fontSize: font.size.body, color: color.text.secondary },
  controls: { display: "flex", alignItems: "flex-end", gap: space.md },
  label: { display: "block", fontSize: font.size.label, fontWeight: font.weight.bold, color: color.text.secondary, marginBottom: 6 },
  select: { background: color.surface.card, border: `1px solid ${color.border.default}`, color: color.text.primary, borderRadius: 6, padding: "8px 10px", fontSize: 13, fontFamily: font.family },
  legend: { display: "flex", flexWrap: "wrap", gap: 14, marginBottom: 12 },
  legendItem: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: font.size.label, color: color.text.secondary, fontWeight: font.weight.bold },
  legendSwatch: { width: 14, height: 14, borderRadius: 3, border: "1px solid", display: "inline-block" },
  empty: { background: color.surface.card, border: `1px solid ${color.border.default}`, borderRadius: 10, padding: "28px 20px", textAlign: "center", color: color.text.secondary, fontSize: font.size.body },
  chart: { position: "relative", background: color.surface.card, border: `1px solid ${color.border.default}`, borderRadius: 10, padding: "10px 12px 14px", overflow: "hidden" },
  nowLine: { position: "absolute", top: 24, bottom: 10, width: 2, background: "#000", zIndex: 6, pointerEvents: "none" },
  nowLabel: { position: "absolute", top: -16, left: "50%", transform: "translateX(-50%)", background: "#000", color: "#fff", fontSize: 10, fontWeight: font.weight.bold, padding: "1px 5px", borderRadius: 4, whiteSpace: "nowrap" },
  axisRow: { display: "flex", alignItems: "flex-end", height: 22, marginBottom: 4 },
  axisTrack: { position: "relative", flex: 1, height: "100%" },
  axisTick: { position: "absolute", bottom: 0, fontSize: 11, color: color.text.muted, whiteSpace: "nowrap" },
  section: { marginTop: 8 },
  sectionHeadRow: { display: "flex", marginBottom: 4 },
  sectionHead: { display: "inline-block", alignSelf: "flex-start", width: "fit-content", fontSize: font.size.label, fontWeight: font.weight.bold, textTransform: "uppercase", letterSpacing: ".6px", padding: "3px 10px", borderRadius: 5, border: "1px solid" },
  row: { display: "flex", alignItems: "center", marginBottom: 3 },
  roomLabel: { flexShrink: 0, display: "flex", alignItems: "baseline", gap: 6, fontSize: font.size.label, fontWeight: font.weight.bold, color: color.text.secondary, whiteSpace: "nowrap", overflow: "hidden", paddingRight: 8 },
  // The room number never shrinks: it is how a reader finds the row.
  roomLabelName: { flexShrink: 0 },
  // ⚠️ NOT ALARM COLOUR. Amber and red on this page mean urgency and emergency;
  // a phase label is a routine fact present on every running room all day, and
  // tinting it with alarm colour is how staff stop reading alarm colour. Navy is
  // the app's informational hue and is in no alarm vocabulary.
  roomLabelPhase: { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", fontSize: 10, fontWeight: 700, letterSpacing: "0.02em", color: color.accent.primary },
  // ⚠️ THE RING BLINKS, NOT THE LABEL. `atcFlash` fades the whole element to
  // 25% opacity, which would make the room number half-invisible for half of
  // every second — on the one row a reader needs to identify under pressure.
  // `atcRing` animates `outline-color` only, so the attention-grab survives and
  // the text never dims (index.js:42-50). Both keyframes are disabled under
  // prefers-reduced-motion; the red fill, the ring and the 🚨 remain, so the
  // room is still unmistakable with no motion at all.
  roomLabelEmergency: {
    color: "#991b1b",
    background: "#fef2f2",
    borderRadius: radius.sm,
    paddingLeft: 6,
    outline: `2px solid ${color.accent.error}`,
    outlineOffset: -1,
    animation: "atcRing 1s steps(1) infinite",
  },
  // Sit in the label gutter beside the room name, sized to the 152px column.
  rowBtns: { display: "flex", gap: 4, alignItems: "center", flexShrink: 0, marginRight: 8 },
  rowBtn: {
    width: 26,
    height: 24,
    padding: 0,
    lineHeight: 1,
    fontSize: 13,
    fontFamily: "inherit",
    color: color.text.secondary,
    background: color.surface.card,
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.sm,
    cursor: "pointer",
  },
  srOnly: { position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap", border: 0 },
  track: { position: "relative", flex: 1, height: "100%", borderRadius: 4 },
  // ⚠️ EVERY `bar*` RULE MOVED TO `components/timelineColumns/CaseBar.js`.
  // The bar is not rendered here any more, and eighty lines of styling for a
  // component this file does not own was the largest single block in it.
  // Staffing strip
  staffWrap: { marginTop: 16 },
  staffTitle: { fontSize: font.size.body, fontWeight: font.weight.bold, color: color.text.primary, marginBottom: 8 },
  staffNote: { fontSize: font.size.label, fontWeight: font.weight.regular, color: color.text.muted },
  // One card per shift; wide enough for the Providers | Doctors columns.
  staffGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 10 },
  // Providers | Doctors columns inside a shift card.
  staffCols: { display: "flex", gap: 10, alignItems: "flex-start" },
  staffColTitle: { fontSize: 10, fontWeight: font.weight.bold, letterSpacing: ".6px", textTransform: "uppercase", color: color.text.muted, marginBottom: 2 },
  soloTag: { fontSize: 9, fontWeight: 800, color: "#b45309", border: "1px solid #b45309", borderRadius: 9999, padding: "0 5px", letterSpacing: ".4px" },
  staffRooms: { fontSize: font.size.label, color: color.text.secondary },
  staffCard: { background: color.surface.card, border: `1px solid ${color.border.default}`, borderLeft: "4px solid", borderTop: "3px solid", borderRadius: 8, padding: "10px 12px" },
  staffCardHead: { display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 4 },
  staffTime: { fontSize: font.size.body, fontWeight: font.weight.bold, color: color.text.primary },
  staffClock: { fontSize: font.size.label, color: color.text.muted },
  staffDelta: { fontSize: 18, fontWeight: font.weight.bold, lineHeight: 1.1, marginBottom: 4 },
  staffMetrics: { display: "flex", alignItems: "center", gap: 6, fontSize: font.size.label, color: color.text.secondary, marginBottom: 6 },
  staffMetric: {},
  staffDot: { color: color.text.muted },
  staffNames: { display: "flex", flexDirection: "column", gap: 2, borderTop: `1px solid ${color.border.default}`, paddingTop: 6 },
  staffNone: { fontSize: font.size.label, color: color.text.muted, fontStyle: "italic" },
  staffName: { fontSize: 11, color: color.text.secondary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  // A CRNA whose departure is imminent.
  //
  // ⚠️ TWO BUGS FIXED HERE, BOTH PREVIOUSLY DOCUMENTED ELSEWHERE IN THIS REPO.
  //
  // 1. It used `accent.error` — a FOURTH consumer of alarm red, on a page whose
  //    red is reserved for an unacknowledged emergency, an uncovered room, and
  //    lapsing cover. A scheduled departure is expected; it is not an alarm.
  //    Spending the alarm colour on it teaches people to discount the colour
  //    that also means a patient has no anesthetist.
  // 2. It flashed with `atcFlash`, which steps opacity to 0.25 — so the blink
  //    HID THE NAME it exists to highlight, exactly half the time, and exactly
  //    when the reader most needs to read it.
  //
  // Weight plus a left rule carries the urgency instead: both survive greyscale,
  // neither borrows the alarm budget, and the name stays on screen continuously.
  staffNameSoon: {
    color: "#7a4f00",
    fontWeight: font.weight.bold,
    borderLeft: `3px solid ${color.accent.closing}`,
    paddingLeft: 5,
  },
  staffNameTime: { color: color.text.muted, fontSize: 10 },
  // The orientee under the person they are with. Its own line, indented, in
  // muted ink — subordinate, because they do not hold the room and the count
  // beside it does not include them.
  staffBench: { color: color.accent.primary, fontSize: 10, fontWeight: font.weight.bold },
};
